/**
 * Elite Batch 21 Part 3 — 27 Workflow Templates
 * S2 Remaining (4): Ticket Creation Bot, Troubleshooting Wizard,
 *   Zendesk Ticket to Slack Alert, Self-Service Article Recommendation
 * S3 Surveys/CSAT/NPS (13): CSAT Survey After Resolution, CSAT Survey After
 *   Support Ticket, CSAT Survey Post Chat, Chatbot Analytics Digest, Customer
 *   Effort Score Tracker, Customer Feedback Survey, Customer Survey Distribution
 *   Bot, NPS Detractor Recovery Workflow, NPS Passive Education Nurture, NPS
 *   Survey After Milestone, NPS Survey Bot, Service Appointment Follow-Up,
 *   WebEngage Feedback Collection Flow
 * S4 Recovery/Churn first 10: Cancellation Prevention Bot, Concierge Chat
 *   Follow-Up, Customer Advisory Board Invite, Customer Communication Preference
 *   Center, Customer Escalation Detector, Customer Service Recovery After Bad
 *   Experience, Health-Score Decline Customer Success Play, Help-Center Article
 *   No-Resolution Follow-Up, High-Risk Customer Internal Account Alert,
 *   Onboarding Support Check-In
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

/* ───────────────────────────────────────────────────────────────────────────
 * S2 REMAINING — 4 WORKFLOWS
 * ─────────────────────────────────────────────────────────────────────────── */

const ticket_creation_bot = {
  nodes: [
    trigger("Webhook: User Starts Ticket Creation in Chat", "webhook_app", {
      source: "chat_widget",
      event: "ticket_creation_started",
      howItStarts:
        "Fires when a user initiates ticket creation through the chat widget or support portal.",
      setup:
        "Connect your chat widget or support portal to send a webhook when a user starts a ticket creation flow.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Extract Issue Details Conversationally",
      280,
      "You are a support ticket intake assistant. Extract structured issue details from the raw chat payload. Identify the subject, description, category (billing, technical, account, other), priority guess (low, normal, high, urgent), and the customer email. If any required field is missing, set missing_fields to a comma-separated list of what is absent. Always output valid JSON.",
      "Analyze this support request and extract structured fields: {webhook_payload}",
      [
        { field: "subject", as: "ticket_subject", type: "string" },
        { field: "description", as: "ticket_description", type: "string" },
        { field: "category", as: "ticket_category", type: "string" },
        { field: "priority", as: "ticket_priority", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "missing_fields", as: "missing_fields", type: "string" },
      ],
    ),
    n("3", "runCode", "Validate Required Fields Present", 520, {
      codeDetails: {
        code: `const subject = inputs.ticket_subject || "";
const description = inputs.ticket_description || "";
const email = inputs.customer_email || "";
const missing = [];
if (!subject || subject === "unknown") missing.push("subject");
if (!description || description.length < 10) missing.push("description");
if (!email || !email.includes("@")) missing.push("email");
output.fields_valid = missing.length === 0;
output.missing_list = missing.join(", ");
output.subject = subject;
output.description = description;
output.email = email;
output.category = inputs.ticket_category || "general";
output.priority = inputs.ticket_priority || "normal";`,
        outputVars: [
          { name: "fields_valid", type: "boolean" },
          { name: "missing_list", type: "string" },
          { name: "subject", type: "string" },
          { name: "description", type: "string" },
          { name: "email", type: "string" },
          { name: "category", type: "string" },
          { name: "priority", type: "string" },
        ],
      },
    }),
    n("4", "hitApi", "Zendesk Create Ticket", 760, {
      hitApiDetails: {
        method: "POST",
        url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
        headers: [
          { key: "Authorization", value: "Basic {zendesk_api_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "ticket",
            value:
              '{"subject":"{subject}","description":"{description}","priority":"{priority}","type":"problem","requester":{"email":"{email}"},"tags":["{category}","auto_created"]}',
          },
        ],
        variables: [
          {
            name: "ticket.id",
            customName: "zendesk_ticket_id",
            type: "number",
          },
          {
            name: "ticket.status",
            customName: "ticket_status",
            type: "string",
          },
          {
            name: "ticket.assignee_id",
            customName: "assignee_id",
            type: "number",
          },
        ],
      },
    }),
    emailNode(
      "5",
      "Email Confirm Ticket Creation to Customer",
      1000,
      "{email}",
      "Your support ticket #{zendesk_ticket_id} has been created",
      "Hi,\\n\\nWe have received your support request and created ticket #{zendesk_ticket_id}.\\n\\nSubject: {subject}\\nCategory: {category}\\nPriority: {priority}\\n\\nOur team will review your request and respond shortly. You can track your ticket status by replying to this email.\\n\\nThank you for reaching out.",
    ),
    slackNode(
      "6",
      "Slack Notify Assigned Support Team",
      1240,
      ":ticket: *New Ticket Created* #{zendesk_ticket_id}\\n*Subject:* {subject}\\n*Priority:* {priority}\\n*Category:* {category}\\n*Requester:* {email}\\n*Source:* Chat widget auto-creation",
    ),
    sheetsNode(
      "7",
      "Log Ticket Creation Source and Completeness",
      1480,
      "TicketCreation",
      '[["{zendesk_ticket_id}","{email}","{subject}","{category}","{priority}","chat_widget","{fields_valid}","{missing_list}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Automates ticket creation from chat widget interactions. An AI node conversationally extracts issue details, validates required fields, creates a structured Zendesk ticket, confirms via email to the customer, alerts the assigned team in Slack, and logs creation source and field completeness to Google Sheets for quality tracking.",
};

const troubleshooting_wizard = {
  nodes: [
    trigger("Webhook: Customer Reports Technical Issue", "webhook_app", {
      source: "support_portal",
      event: "technical_issue_reported",
      howItStarts:
        "Fires when a customer submits a technical issue report through the support portal or chat.",
      setup:
        "Connect your support portal to send a webhook when a customer reports a technical issue.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Classify Issue Type and Select Diagnostic Tree",
      280,
      "You are a technical support diagnostic expert. Classify the reported issue into one of these types: connectivity, performance, authentication, data_sync, ui_bug, integration, or other. Select the appropriate diagnostic decision tree and generate the first diagnostic step as a clear instruction the customer can follow. Output: issue_type, severity (low/medium/high/critical), first_diagnostic_step (a clear instruction), expected_outcome (what success looks like).",
      "Classify this technical issue and generate the first diagnostic step: {webhook_payload}",
      [
        { field: "issue_type", as: "issue_type", type: "string" },
        { field: "severity", as: "severity", type: "string" },
        { field: "first_diagnostic_step", as: "first_step", type: "string" },
        { field: "expected_outcome", as: "expected_outcome", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
      ],
    ),
    n("3", "runCode", "Execute First Diagnostic Step", 520, {
      codeDetails: {
        code: `const issueType = inputs.issue_type || "other";
const diagnosticResults = {
  connectivity: { check: "DNS resolution and port availability", auto_fixable: true },
  performance: { check: "Response time and resource utilization", auto_fixable: false },
  authentication: { check: "Token validity and SSO configuration", auto_fixable: true },
  data_sync: { check: "Last sync timestamp and queue depth", auto_fixable: false },
  ui_bug: { check: "Browser compatibility and cache state", auto_fixable: true },
  integration: { check: "API endpoint health and credential validity", auto_fixable: false },
  other: { check: "General system health", auto_fixable: false },
};
const result = diagnosticResults[issueType] || diagnosticResults.other;
output.diagnostic_check = result.check;
output.auto_fixable = result.auto_fixable;
output.step_number = 1;
output.resolved_by_first = result.auto_fixable;
output.diagnostic_history = "Step 1: " + result.check + " — Result: " + (result.auto_fixable ? "Auto-fix applied" : "Requires further investigation");`,
        outputVars: [
          { name: "diagnostic_check", type: "string" },
          { name: "auto_fixable", type: "boolean" },
          { name: "step_number", type: "number" },
          { name: "resolved_by_first", type: "boolean" },
          { name: "diagnostic_history", type: "string" },
        ],
      },
    }),
    n("4", "condition", "Issue Resolved by First Step?", 760, {
      conditionDetails: {
        conditions: [
          {
            variable: "{resolved_by_first}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    aiNode(
      "5",
      "AI Generate Next Diagnostic Step",
      1000,
      "You are a technical support diagnostic expert continuing troubleshooting. Based on the issue type, severity, and what has been tried so far, generate the next diagnostic step. Be specific and actionable. Output: next_step (clear instruction), rationale (why this step), escalation_needed (true/false).",
      "Issue type: {issue_type}, Severity: {severity}, Diagnostic history so far: {diagnostic_history}. Generate the next diagnostic step.",
      [
        { field: "next_step", as: "next_step", type: "string" },
        { field: "rationale", as: "step_rationale", type: "string" },
        { field: "escalation_needed", as: "escalation_needed", type: "string" },
      ],
      400,
    ),
    n(
      "6",
      "sendMessage",
      "Guide Customer Through Resolution",
      1240,
      {
        sendMessageDetails: {
          message:
            "We have diagnosed your {issue_type} issue.\\n\\nDiagnostic step: {next_step}\\n\\nRationale: {step_rationale}\\n\\nPlease try this and let us know the result. If the issue persists, we will escalate to our engineering team with the full diagnostic history.",
        },
      },
      400,
    ),
    n(
      "7",
      "condition",
      "Resolved After Additional Steps?",
      1480,
      {
        conditionDetails: {
          conditions: [
            {
              variable: "{escalation_needed}",
              operator: "equals",
              value: "false",
            },
          ],
        },
      },
      400,
    ),
    n(
      "8",
      "hitApi",
      "Jira Create Ticket with Diagnostic History",
      1720,
      {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"ENG"},"summary":"Escalation: {issue_type} issue — unresolved after diagnostics","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Issue Type: {issue_type}\\nSeverity: {severity}\\nCustomer: {customer_email}\\nDiagnostic History:\\n{diagnostic_history}\\nLast Step Attempted: {next_step}\\nResult: Unresolved — requires engineering investigation"}]}]},"issuetype":{"name":"Bug"},"priority":{"name":"High"},"labels":["auto-escalated","troubleshooting-wizard"]}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_issue_id", type: "string" },
            { name: "key", customName: "jira_issue_key", type: "string" },
          ],
        },
      },
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_2"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8", "output_2"),
  ],
  summary:
    "A multi-step troubleshooting wizard for technical issues. AI classifies the issue type and selects the diagnostic tree, runs the first diagnostic step automatically, branches on resolution, generates follow-up diagnostic steps if needed, guides the customer through the process, and escalates unresolved issues to Jira with the complete diagnostic history for engineering review.",
};

const zendesk_ticket_to_slack_alert = {
  nodes: [
    trigger(
      "Webhook: Zendesk Ticket Created or Priority Changed",
      "webhook_app",
      {
        source: "zendesk",
        event: "ticket.created_or_priority_changed",
        howItStarts:
          "Fires when a new Zendesk ticket is created or an existing ticket's priority is changed.",
        setup:
          "Configure a Zendesk webhook trigger for ticket creation and priority change events.",
        inputFields: [],
      },
    ),
    aiNode(
      "2",
      "AI Extract Ticket Details",
      280,
      "You are a Zendesk webhook parser. Extract the ticket ID, subject, priority (urgent, high, normal, low), requester name and email, assignee name, and the assigned team or group. If the event is a priority change, also extract the previous priority. Output all fields as JSON.",
      "Parse this Zendesk webhook and extract ticket details: {webhook_payload}",
      [
        { field: "ticket_id", as: "ticket_id", type: "string" },
        { field: "subject", as: "ticket_subject", type: "string" },
        { field: "priority", as: "ticket_priority", type: "string" },
        { field: "requester_email", as: "requester_email", type: "string" },
        { field: "requester_name", as: "requester_name", type: "string" },
        { field: "assignee_name", as: "assignee_name", type: "string" },
        { field: "team", as: "assigned_team", type: "string" },
      ],
    ),
    n("3", "runCode", "Determine Slack Channel by Team and Priority", 520, {
      codeDetails: {
        code: `const team = (inputs.assigned_team || "general").toLowerCase();
const priority = (inputs.ticket_priority || "normal").toLowerCase();
const teamChannels = {
  billing: "#support-billing",
  technical: "#support-engineering",
  account: "#support-accounts",
  sales: "#support-sales",
  general: "#support-general",
};
const channel = teamChannels[team] || teamChannels.general;
const isUrgent = priority === "urgent" || priority === "high";
const finalChannel = isUrgent ? "#support-urgent" : channel;
// Rate limiting: track alerts per channel
const now = Date.now();
output.slack_channel = finalChannel;
output.is_urgent = isUrgent;
output.alert_timestamp = new Date(now).toISOString();
output.team_channel = channel;`,
        outputVars: [
          { name: "slack_channel", type: "string" },
          { name: "is_urgent", type: "boolean" },
          { name: "alert_timestamp", type: "string" },
          { name: "team_channel", type: "string" },
        ],
      },
    }),
    n("4", "condition", "Priority Is Urgent or High?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{is_urgent}", operator: "equals", value: "true" },
        ],
      },
    }),
    slackNode(
      "5",
      "Slack Post Immediate Urgent Alert",
      1000,
      ":rotating_light: *URGENT TICKET* #{ticket_id}\\n*Subject:* {ticket_subject}\\n*Priority:* {ticket_priority}\\n*Requester:* {requester_name} ({requester_email})\\n*Assigned to:* {assignee_name}\\n*Channel:* {slack_channel}\\n<https://{zendesk_subdomain}.zendesk.com/agent/tickets/{ticket_id}|View in Zendesk>",
    ),
    n(
      "6",
      "delayAction",
      "Wait 2 Hours for Normal Ticket Digest",
      1000,
      {
        delayDetails: { duration: 2, unit: "hours" },
      },
      400,
    ),
    slackNode(
      "7",
      "Slack Send Normal-Priority Ticket Digest",
      1240,
      ":ticket: *New Ticket* #{ticket_id}\\n*Subject:* {ticket_subject}\\n*Priority:* {ticket_priority}\\n*Requester:* {requester_name}\\n*Assigned to:* {assignee_name}\\n*Team:* {assigned_team}\\n<https://{zendesk_subdomain}.zendesk.com/agent/tickets/{ticket_id}|View in Zendesk>",
      400,
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("4", "6", "output_2"),
    e("6", "7"),
  ],
  summary:
    "Routes Zendesk ticket alerts to the appropriate Slack channel based on team assignment and priority level. Urgent and high-priority tickets get immediate alerts in #support-urgent, while normal-priority tickets are batched and sent after a 2-hour delay as a digest to the team-specific channel.",
};

const self_service_article_recommendation = {
  nodes: [
    trigger("Webhook: New Support Ticket Created", "webhook_app", {
      source: "zendesk",
      event: "ticket.created",
      howItStarts:
        "Fires when a new support ticket is created in Zendesk, before agent assignment.",
      setup:
        "Configure a Zendesk trigger to fire a webhook immediately when a new ticket is created.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Analyze Ticket and Extract Problem Description",
      280,
      "You are a support content matching expert. Analyze the incoming ticket and extract: a concise problem_description suitable for semantic search (max 200 chars), the product_area (billing, integrations, authentication, reporting, api, general), customer_email, and ticket_id. Output as JSON.",
      "Analyze this support ticket and extract a searchable problem description: {webhook_payload}",
      [
        {
          field: "problem_description",
          as: "problem_description",
          type: "string",
        },
        { field: "product_area", as: "product_area", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "ticket_id", as: "ticket_id", type: "string" },
      ],
    ),
    n("3", "hitApi", "Pinecone Search Help Center Articles", 520, {
      hitApiDetails: {
        method: "POST",
        url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_environment}.pinecone.io/query",
        headers: [
          { key: "Api-Key", value: "{pinecone_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "namespace", value: "help_center" },
          { key: "topK", value: "5" },
          { key: "includeMetadata", value: "true" },
          { key: "vector", value: "{problem_description_embedding}" },
        ],
        variables: [
          { name: "matches[0].score", customName: "top_score", type: "number" },
          {
            name: "matches[0].metadata.title",
            customName: "article_1_title",
            type: "string",
          },
          {
            name: "matches[0].metadata.url",
            customName: "article_1_url",
            type: "string",
          },
          {
            name: "matches[1].metadata.title",
            customName: "article_2_title",
            type: "string",
          },
          {
            name: "matches[1].metadata.url",
            customName: "article_2_url",
            type: "string",
          },
          {
            name: "matches[2].metadata.title",
            customName: "article_3_title",
            type: "string",
          },
          {
            name: "matches[2].metadata.url",
            customName: "article_3_url",
            type: "string",
          },
        ],
      },
    }),
    n("4", "runCode", "Score Article Relevance and Select Top 3", 760, {
      codeDetails: {
        code: `const topScore = parseFloat(inputs.top_score) || 0;
const articles = [];
if (inputs.article_1_title && inputs.article_1_url) articles.push({ title: inputs.article_1_title, url: inputs.article_1_url });
if (inputs.article_2_title && inputs.article_2_url) articles.push({ title: inputs.article_2_title, url: inputs.article_2_url });
if (inputs.article_3_title && inputs.article_3_url) articles.push({ title: inputs.article_3_title, url: inputs.article_3_url });
output.high_relevance = topScore > 0.8;
output.top_relevance_score = topScore;
output.article_count = articles.length;
output.articles_formatted = articles.map((a, i) => (i + 1) + ". " + a.title + "\\n   " + a.url).join("\\n");
output.article_1_title = articles[0] ? articles[0].title : "N/A";
output.article_1_url = articles[0] ? articles[0].url : "";`,
        outputVars: [
          { name: "high_relevance", type: "boolean" },
          { name: "top_relevance_score", type: "number" },
          { name: "article_count", type: "number" },
          { name: "articles_formatted", type: "string" },
          { name: "article_1_title", type: "string" },
          { name: "article_1_url", type: "string" },
        ],
      },
    }),
    n("5", "condition", "Top Article Relevance > 0.8?", 1000, {
      conditionDetails: {
        conditions: [
          { variable: "{high_relevance}", operator: "equals", value: "true" },
        ],
      },
    }),
    emailNode(
      "6",
      "Email Recommended Articles to Customer",
      1240,
      "{customer_email}",
      "We found articles that might help with your request (Ticket #{ticket_id})",
      "Hi,\\n\\nWhile our team reviews your support request, we found some articles that may help resolve your issue right away:\\n\\n{articles_formatted}\\n\\nIf these articles resolve your issue, simply reply to let us know. Otherwise, our support team will be with you shortly.\\n\\nBest regards,\\nSupport Team",
    ),
    sheetsNode(
      "7",
      "Track Self-Resolution Rates by Article",
      1480,
      "SelfResolution",
      '[["{ticket_id}","{customer_email}","{product_area}","{problem_description}","{article_1_title}","{top_relevance_score}","{high_relevance}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("6", "7"),
  ],
  summary:
    "Recommends self-service help center articles before an agent responds. When a ticket is created, AI extracts the problem description, searches Pinecone for semantically similar articles, scores relevance, and if a strong match is found (>0.8), emails the top 3 articles to the customer. Tracks self-resolution rates by article to optimize the help center.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * S3 — SURVEYS / CSAT / NPS — 13 WORKFLOWS
 * ─────────────────────────────────────────────────────────────────────────── */

const csat_survey_after_resolution = {
  nodes: [
    trigger("Webhook: Ticket Resolved in Zendesk", "webhook_app", {
      source: "zendesk",
      event: "ticket.resolved",
      howItStarts:
        "Fires when a support ticket status changes to resolved in Zendesk.",
      setup:
        "Configure a Zendesk trigger to fire a webhook when ticket status changes to resolved.",
      inputFields: [],
    }),
    n(
      "2",
      "runCode",
      "Check Survey Fatigue: Not Surveyed in Last 30 Days",
      280,
      {
        codeDetails: {
          code: `// Check if customer was surveyed in the last 30 days
const email = inputs.customer_email || "";
const ticketId = inputs.ticket_id || "";
const agentName = inputs.agent_name || "Unknown";
const category = inputs.ticket_category || "general";
const lastSurveyDate = inputs.last_survey_date || "";
const now = Date.now();
const thirtyDaysAgo = now - (30 * 24 * 60 * 60 * 1000);
const lastSurveyTs = lastSurveyDate ? new Date(lastSurveyDate).getTime() : 0;
const eligible = !lastSurveyDate || lastSurveyTs < thirtyDaysAgo;
output.survey_eligible = eligible;
output.customer_email = email;
output.ticket_id = ticketId;
output.agent_name = agentName;
output.ticket_category = category;
output.days_since_last_survey = lastSurveyDate ? Math.floor((now - lastSurveyTs) / (24*60*60*1000)) : 999;`,
          outputVars: [
            { name: "survey_eligible", type: "boolean" },
            { name: "customer_email", type: "string" },
            { name: "ticket_id", type: "string" },
            { name: "agent_name", type: "string" },
            { name: "ticket_category", type: "string" },
            { name: "days_since_last_survey", type: "number" },
          ],
        },
      },
    ),
    n("3", "condition", "Eligible for Survey?", 520, {
      conditionDetails: {
        conditions: [
          { variable: "{survey_eligible}", operator: "equals", value: "true" },
        ],
      },
    }),
    n("4", "delayAction", "Wait 2 Hours After Resolution", 760, {
      delayDetails: { duration: 2, unit: "hours" },
    }),
    emailNode(
      "5",
      "Email CSAT Survey with 1-5 Rating",
      1000,
      "{customer_email}",
      "How did we do? Rate your support experience (Ticket #{ticket_id})",
      "Hi,\\n\\nYour support ticket #{ticket_id} was recently resolved. We would love your feedback!\\n\\nHow would you rate your experience? (1 = Very Unsatisfied, 5 = Very Satisfied)\\n\\n1 - Very Unsatisfied: {survey_base_url}/csat?ticket={ticket_id}&score=1\\n2 - Unsatisfied: {survey_base_url}/csat?ticket={ticket_id}&score=2\\n3 - Neutral: {survey_base_url}/csat?ticket={ticket_id}&score=3\\n4 - Satisfied: {survey_base_url}/csat?ticket={ticket_id}&score=4\\n5 - Very Satisfied: {survey_base_url}/csat?ticket={ticket_id}&score=5\\n\\nYour feedback helps us improve. You can also share additional comments on the survey page.\\n\\nThank you!",
    ),
    aiNode(
      "6",
      "AI Analyze Response Sentiment and Themes",
      1240,
      "You are a customer feedback analyst. Analyze the CSAT survey response including the numeric score and any open-text feedback. Identify sentiment (positive, neutral, negative), key themes (response_time, knowledge, empathy, resolution_quality, communication), and generate a brief summary. Output: sentiment, themes (comma-separated), summary, score (number 1-5).",
      "Analyze this CSAT response — Score: {csat_score}, Feedback: {csat_feedback}, Ticket: {ticket_id}, Agent: {agent_name}",
      [
        { field: "sentiment", as: "response_sentiment", type: "string" },
        { field: "themes", as: "feedback_themes", type: "string" },
        { field: "summary", as: "feedback_summary", type: "string" },
        { field: "score", as: "csat_score_final", type: "number" },
      ],
    ),
    sheetsNode(
      "7",
      "Log CSAT Score with Attribution",
      1480,
      "CSATResults",
      '[["{ticket_id}","{customer_email}","{agent_name}","{ticket_category}","{csat_score_final}","{response_sentiment}","{feedback_themes}","{feedback_summary}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Sends a CSAT survey 2 hours after ticket resolution with survey fatigue prevention. Checks that the customer has not been surveyed in the last 30 days, emails a 1-5 rating survey, uses AI to analyze response sentiment and themes, and logs scores with full attribution to ticket, agent, and category in Google Sheets.",
};

const csat_survey_after_support_ticket = {
  nodes: [
    trigger("Webhook: Ticket Closed in Zendesk", "webhook_app", {
      source: "zendesk",
      event: "ticket.closed",
      howItStarts:
        "Fires when a support ticket is fully closed in Zendesk (after resolution confirmation).",
      setup:
        "Configure a Zendesk trigger for ticket closure events to fire this webhook.",
      inputFields: [],
    }),
    n(
      "2",
      "runCode",
      "Verify Eligibility: Ticket Type, Resolution, Fatigue Window",
      280,
      {
        codeDetails: {
          code: `const ticketId = inputs.ticket_id || "";
const email = inputs.customer_email || "";
const ticketType = inputs.ticket_type || "problem";
const resolution = inputs.resolution_type || "solved";
const lastSurvey = inputs.last_survey_date || "";
// Exclude internal tickets, spam, and recently surveyed customers
const excludedTypes = ["internal", "spam", "test"];
const validResolutions = ["solved", "resolved", "workaround"];
const typeOk = !excludedTypes.includes(ticketType.toLowerCase());
const resolutionOk = validResolutions.includes(resolution.toLowerCase());
const now = Date.now();
const fatigueOk = !lastSurvey || (now - new Date(lastSurvey).getTime()) > (30 * 24 * 60 * 60 * 1000);
output.eligible = typeOk && resolutionOk && fatigueOk;
output.ticket_id = ticketId;
output.customer_email = email;
output.ticket_type = ticketType;
output.rejection_reason = !typeOk ? "excluded_type" : (!resolutionOk ? "invalid_resolution" : (!fatigueOk ? "survey_fatigue" : "none"));`,
          outputVars: [
            { name: "eligible", type: "boolean" },
            { name: "ticket_id", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "ticket_type", type: "string" },
            { name: "rejection_reason", type: "string" },
          ],
        },
      },
    ),
    n("3", "condition", "Eligible for CSAT Survey?", 520, {
      conditionDetails: {
        conditions: [
          { variable: "{eligible}", operator: "equals", value: "true" },
        ],
      },
    }),
    emailNode(
      "4",
      "Email CSAT Survey via Outgrow Forms Link",
      760,
      "{customer_email}",
      "Quick feedback on your support experience (Ticket #{ticket_id})",
      "Hi,\\n\\nWe recently resolved your support ticket #{ticket_id}. We would appreciate 30 seconds of your time to share your experience.\\n\\nPlease click below to rate your experience:\\nhttps://yourcompany.outgrow-forms.com/csat-survey?ticket_id={ticket_id}&email={customer_email}\\n\\nYour feedback directly helps us improve our support quality.\\n\\nThank you!",
    ),
    n("5", "delayAction", "Wait 72 Hours for Response", 1000, {
      delayDetails: { duration: 72, unit: "hours" },
    }),
    n("6", "runCode", "Collect Response from Outgrow Forms API", 1240, {
      codeDetails: {
        code: `// Fetch Outgrow Forms response for this ticket
const ticketId = inputs.ticket_id || "";
const email = inputs.customer_email || "";
// Response data would come from Outgrow Forms API poll
const score = inputs.outgrow-forms_score || 0;
const feedback = inputs.outgrow-forms_feedback || "";
const responded = score > 0;
output.responded = responded;
output.csat_score = score;
output.feedback_text = feedback;
output.ticket_id = ticketId;
output.customer_email = email;
output.response_time_hours = 72;`,
        outputVars: [
          { name: "responded", type: "boolean" },
          { name: "csat_score", type: "number" },
          { name: "feedback_text", type: "string" },
          { name: "ticket_id", type: "string" },
          { name: "customer_email", type: "string" },
          { name: "response_time_hours", type: "number" },
        ],
      },
    }),
    sheetsNode(
      "7",
      "Log Response with Ticket Attribution",
      1480,
      "CSATTicketSurvey",
      '[["{ticket_id}","{customer_email}","{ticket_type}","{csat_score}","{feedback_text}","{responded}","{response_time_hours}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Sends a CSAT survey via Outgrow Forms after a support ticket is closed. Verifies eligibility by checking ticket type, resolution quality, and survey fatigue window. Waits 72 hours for the response, then collects the Outgrow Forms data and logs scores with full ticket attribution to Google Sheets for analysis.",
};

const csat_survey_post_chat = {
  nodes: [
    trigger("Webhook: Live Chat Session Ended", "webhook_app", {
      source: "chat_platform",
      event: "chat.ended",
      howItStarts:
        "Fires when a live chat conversation is completed or ended by either party.",
      setup:
        "Configure your live chat platform (Intercom, Drift, Zendesk Chat) to send a webhook when a chat session ends.",
      inputFields: [],
    }),
    n("2", "runCode", "Check Chat Resolution and Fatigue Rules", 280, {
      codeDetails: {
        code: `const chatId = inputs.chat_id || "";
const email = inputs.customer_email || "";
const hadResolution = inputs.resolution_provided || "true";
const chatDuration = parseInt(inputs.chat_duration_seconds || "60");
const lastSurvey = inputs.last_survey_date || "";
// Must be a real conversation (>30s) with resolution attempt
const meaningfulChat = chatDuration > 30;
const resolved = hadResolution === "true";
const now = Date.now();
const fatigueOk = !lastSurvey || (now - new Date(lastSurvey).getTime()) > (14 * 24 * 60 * 60 * 1000);
output.eligible = meaningfulChat && fatigueOk;
output.chat_id = chatId;
output.customer_email = email;
output.had_resolution = resolved;
output.chat_duration_seconds = chatDuration;`,
        outputVars: [
          { name: "eligible", type: "boolean" },
          { name: "chat_id", type: "string" },
          { name: "customer_email", type: "string" },
          { name: "had_resolution", type: "boolean" },
          { name: "chat_duration_seconds", type: "number" },
        ],
      },
    }),
    n("3", "condition", "Eligible for Post-Chat Survey?", 520, {
      conditionDetails: {
        conditions: [
          { variable: "{eligible}", operator: "equals", value: "true" },
        ],
      },
    }),
    n("4", "sendMessage", "In-Chat CSAT Prompt with Quick-Rate Buttons", 760, {
      sendMessageDetails: {
        message:
          "Thank you for chatting with us! Before you go, how would you rate this experience?\\n\\n:star: 1 - Poor  |  :star::star: 2 - Fair  |  :star::star::star: 3 - OK  |  :star::star::star::star: 4 - Good  |  :star::star::star::star::star: 5 - Excellent\\n\\nFeel free to add any comments about your experience.",
      },
    }),
    aiNode(
      "5",
      "AI Analyze Rating and Open-Text Feedback",
      1000,
      "You are a customer experience analyst. Analyze the in-chat CSAT rating (1-5) and any open-text feedback provided. Identify: the numeric score, sentiment (positive/neutral/negative), key themes (wait_time, agent_knowledge, resolution_speed, friendliness, clarity), and a brief summary. Output as JSON.",
      "Analyze this post-chat feedback — Chat ID: {chat_id}, Rating: {chat_rating}, Comments: {chat_feedback}",
      [
        { field: "score", as: "csat_score", type: "number" },
        { field: "sentiment", as: "chat_sentiment", type: "string" },
        { field: "themes", as: "feedback_themes", type: "string" },
        { field: "summary", as: "feedback_summary", type: "string" },
      ],
    ),
    n("6", "condition", "Score <= 2 (Negative Experience)?", 1240, {
      conditionDetails: {
        conditions: [
          {
            variable: "{csat_score}",
            operator: "less_than_or_equal",
            value: "2",
          },
        ],
      },
    }),
    slackNode(
      "7",
      "Slack Alert Team Lead About Negative Chat",
      1480,
      ":warning: *Negative Chat Experience Alert*\\n*Chat ID:* {chat_id}\\n*Customer:* {customer_email}\\n*CSAT Score:* {csat_score}/5\\n*Sentiment:* {chat_sentiment}\\n*Themes:* {feedback_themes}\\n*Summary:* {feedback_summary}\\n*Chat Duration:* {chat_duration_seconds}s\\nPlease review and follow up with the customer.",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_1"),
  ],
  summary:
    "Prompts an in-chat CSAT survey immediately after a live chat session ends. Checks eligibility based on chat duration and fatigue rules, presents a quick-rate button prompt in the chat, uses AI to analyze rating and open-text feedback, and alerts the team lead in Slack when a score of 2 or below indicates a negative experience.",
};

const chatbot_analytics_digest = {
  nodes: [
    trigger("Schedule: Weekly on Fridays at 9 AM", "schedule", {
      schedule: "0 9 * * 5",
      howItStarts:
        "Runs every Friday at 9 AM to generate a weekly chatbot analytics digest.",
      setup:
        "No external setup needed. The schedule automatically triggers weekly analysis.",
      inputFields: [],
    }),
    n(
      "2",
      "hitApi",
      "Fetch Sampled Chatbot Conversations from Past Week",
      280,
      {
        hitApiDetails: {
          method: "GET",
          url: "https://api.intercom.io/conversations?per_page=50&order=desc&sort=created_at",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "Intercom-Version", value: "2.10" },
          ],
          body: [],
          variables: [
            {
              name: "conversations",
              customName: "weekly_conversations",
              type: "string",
            },
            {
              name: "total_count",
              customName: "total_conversation_count",
              type: "number",
            },
            {
              name: "pages.total_pages",
              customName: "total_pages",
              type: "number",
            },
          ],
        },
      },
    ),
    aiNode(
      "3",
      "AI Analyze Conversation Themes and Patterns",
      520,
      "You are a chatbot analytics expert. Analyze the sampled conversations and identify: top_themes (list of top 5 conversation topics with count), resolution_rate (percentage of conversations resolved without human handoff), failure_patterns (top 3 patterns where the chatbot failed), avg_turns (average number of conversation turns), and improvement_suggestions (top 3 actionable suggestions). Output as JSON.",
      "Analyze these chatbot conversations from the past week. Total conversations: {total_conversation_count}. Sample data: {weekly_conversations}",
      [
        { field: "top_themes", as: "top_themes", type: "string" },
        { field: "resolution_rate", as: "resolution_rate", type: "string" },
        { field: "failure_patterns", as: "failure_patterns", type: "string" },
        { field: "avg_turns", as: "avg_turns", type: "number" },
        {
          field: "improvement_suggestions",
          as: "improvement_suggestions",
          type: "string",
        },
      ],
    ),
    n(
      "4",
      "runCode",
      "Compute Containment Rate and Top Unresolved Topics",
      760,
      {
        codeDetails: {
          code: `const totalConversations = parseInt(inputs.total_conversation_count) || 0;
const resolutionRate = parseFloat(inputs.resolution_rate) || 0;
const containmentRate = resolutionRate;
const handoffRate = 100 - containmentRate;
const avgTurns = parseFloat(inputs.avg_turns) || 0;
output.containment_rate = containmentRate.toFixed(1) + "%";
output.handoff_rate = handoffRate.toFixed(1) + "%";
output.total_conversations = totalConversations;
output.avg_turns = avgTurns;
output.week_label = new Date().toISOString().split("T")[0];
output.needs_attention = containmentRate < 70;`,
          outputVars: [
            { name: "containment_rate", type: "string" },
            { name: "handoff_rate", type: "string" },
            { name: "total_conversations", type: "number" },
            { name: "avg_turns", type: "number" },
            { name: "week_label", type: "string" },
            { name: "needs_attention", type: "boolean" },
          ],
        },
      },
    ),
    slackNode(
      "5",
      "Slack Post Weekly Digest with Metrics",
      1000,
      ":robot_face: *Weekly Chatbot Analytics Digest — {week_label}*\\n\\n:bar_chart: *Metrics*\\n- Total Conversations: {total_conversations}\\n- Containment Rate: {containment_rate}\\n- Handoff Rate: {handoff_rate}\\n- Avg Turns per Conversation: {avg_turns}\\n\\n:speech_balloon: *Top Themes:* {top_themes}\\n\\n:x: *Failure Patterns:* {failure_patterns}\\n\\n:bulb: *Improvement Suggestions:* {improvement_suggestions}",
    ),
    aiNode(
      "6",
      "AI Generate Action Tickets for Top Gaps",
      1240,
      "You are a product operations expert. Based on the chatbot failure patterns and improvement suggestions, generate 2-3 actionable Jira ticket descriptions. Each should have: title, description, priority (high/medium/low), and assignee_team (engineering, content, product). Output as JSON with a tickets array.",
      "Failure patterns: {failure_patterns}. Improvement suggestions: {improvement_suggestions}. Containment rate: {containment_rate}. Generate actionable tickets.",
      [
        { field: "tickets", as: "action_tickets", type: "string" },
        { field: "ticket_count", as: "action_ticket_count", type: "number" },
      ],
    ),
    sheetsNode(
      "7",
      "Track Week-Over-Week Trends",
      1480,
      "ChatbotAnalytics",
      '[["{week_label}","{total_conversations}","{containment_rate}","{handoff_rate}","{avg_turns}","{top_themes}","{failure_patterns}","{action_ticket_count}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Generates a weekly chatbot analytics digest every Friday. Fetches sampled conversations from Intercom, uses AI to analyze themes, resolution rates, and failure patterns, computes containment metrics, posts a comprehensive digest to Slack, generates actionable improvement tickets, and tracks week-over-week trends in Google Sheets.",
};

const customer_effort_score_tracker = {
  nodes: [
    trigger("Webhook: Customer Completes Support Interaction", "webhook_app", {
      source: "support_platform",
      event: "interaction.completed",
      howItStarts:
        "Fires when a customer completes any support interaction (ticket resolved, chat ended, call completed).",
      setup:
        "Configure your support platform to send a webhook after each completed customer interaction.",
      inputFields: [],
    }),
    emailNode(
      "2",
      "Email CES Survey: How Easy Was It?",
      280,
      "{customer_email}",
      "One quick question about your recent support experience",
      "Hi,\\n\\nWe would love to know: How easy was it to resolve your issue?\\n\\nPlease rate on a scale of 1-7 (1 = Very Easy, 7 = Very Difficult):\\n\\n1 - Very Easy: {survey_url}/ces?id={interaction_id}&score=1\\n2 - Easy: {survey_url}/ces?id={interaction_id}&score=2\\n3 - Somewhat Easy: {survey_url}/ces?id={interaction_id}&score=3\\n4 - Neutral: {survey_url}/ces?id={interaction_id}&score=4\\n5 - Somewhat Difficult: {survey_url}/ces?id={interaction_id}&score=5\\n6 - Difficult: {survey_url}/ces?id={interaction_id}&score=6\\n7 - Very Difficult: {survey_url}/ces?id={interaction_id}&score=7\\n\\nYour feedback helps us make support easier for everyone.",
    ),
    n("3", "delayAction", "Wait 48 Hours for Response", 520, {
      delayDetails: { duration: 48, unit: "hours" },
    }),
    n("4", "runCode", "Collect CES Response and Compute Score", 760, {
      codeDetails: {
        code: `const score = parseInt(inputs.ces_score) || 0;
const interactionId = inputs.interaction_id || "";
const channel = inputs.interaction_channel || "unknown";
const issueType = inputs.issue_type || "general";
const email = inputs.customer_email || "";
const responded = score > 0;
const highEffort = score > 5;
const effortLevel = score <= 2 ? "low" : (score <= 4 ? "medium" : "high");
output.ces_score = score;
output.responded = responded;
output.high_effort = highEffort;
output.effort_level = effortLevel;
output.interaction_id = interactionId;
output.channel = channel;
output.issue_type = issueType;
output.customer_email = email;`,
        outputVars: [
          { name: "ces_score", type: "number" },
          { name: "responded", type: "boolean" },
          { name: "high_effort", type: "boolean" },
          { name: "effort_level", type: "string" },
          { name: "interaction_id", type: "string" },
          { name: "channel", type: "string" },
          { name: "issue_type", type: "string" },
          { name: "customer_email", type: "string" },
        ],
      },
    }),
    n("5", "condition", "CES Score > 5 (High Effort)?", 1000, {
      conditionDetails: {
        conditions: [
          { variable: "{high_effort}", operator: "equals", value: "true" },
        ],
      },
    }),
    aiNode(
      "6",
      "AI Analyze Effort Drivers by Event Type",
      1240,
      "You are a customer effort analyst. Analyze the high-effort CES response and identify: effort_drivers (what made this difficult — process complexity, transfers, wait time, unclear information, tool limitations), root_cause (the primary reason for high effort), recommended_owner (cs_team, product_team, or support_team), and suggested_fix (specific actionable improvement). Output as JSON.",
      "High-effort CES response — Score: {ces_score}/7, Channel: {channel}, Issue Type: {issue_type}, Interaction: {interaction_id}, Customer: {customer_email}",
      [
        { field: "effort_drivers", as: "effort_drivers", type: "string" },
        { field: "root_cause", as: "root_cause", type: "string" },
        { field: "recommended_owner", as: "recommended_owner", type: "string" },
        { field: "suggested_fix", as: "suggested_fix", type: "string" },
      ],
    ),
    slackNode(
      "7",
      "Slack Route High-Effort Case to Responsible Team",
      1480,
      ":warning: *High Customer Effort Detected*\\n*CES Score:* {ces_score}/7 (High Effort)\\n*Channel:* {channel}\\n*Issue Type:* {issue_type}\\n*Customer:* {customer_email}\\n*Effort Drivers:* {effort_drivers}\\n*Root Cause:* {root_cause}\\n*Recommended Owner:* {recommended_owner}\\n*Suggested Fix:* {suggested_fix}\\nPlease investigate and action.",
    ),
    sheetsNode(
      "8",
      "Trend CES by Channel and Issue Type",
      1720,
      "CESTracker",
      '[["{interaction_id}","{customer_email}","{channel}","{issue_type}","{ces_score}","{effort_level}","{effort_drivers}","{root_cause}","{recommended_owner}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Tracks Customer Effort Score (CES) after every support interaction. Sends a 1-7 CES survey, waits 48 hours for response, identifies high-effort experiences (score >5), uses AI to analyze effort drivers and root causes, routes high-effort cases to the responsible team in Slack, and trends CES by channel and issue type in Google Sheets.",
};

const customer_feedback_survey = {
  nodes: [
    trigger("Webhook: Customer Milestone Event", "webhook_app", {
      source: "crm",
      event: "customer.milestone_reached",
      howItStarts:
        "Fires when a customer reaches a milestone event such as first purchase, onboarding completion, or renewal.",
      setup:
        "Configure your CRM or product platform to send webhooks on milestone events (purchase, onboarding, renewal).",
      inputFields: [],
    }),
    n("2", "runCode", "Check Survey Eligibility and Select Template", 280, {
      codeDetails: {
        code: `const email = inputs.customer_email || "";
const milestoneType = inputs.milestone_type || "general";
const lastSurvey = inputs.last_survey_date || "";
const now = Date.now();
const fatigueOk = !lastSurvey || (now - new Date(lastSurvey).getTime()) > (60 * 24 * 60 * 60 * 1000);
const templateMap = {
  purchase: { form_id: "PURCHASE_FEEDBACK_FORM", subject: "How was your purchase experience?" },
  onboarding: { form_id: "ONBOARDING_FEEDBACK_FORM", subject: "How is your onboarding going?" },
  renewal: { form_id: "RENEWAL_FEEDBACK_FORM", subject: "Thanks for renewing — share your thoughts" },
  general: { form_id: "GENERAL_FEEDBACK_FORM", subject: "We value your feedback" },
};
const template = templateMap[milestoneType] || templateMap.general;
output.eligible = fatigueOk;
output.customer_email = email;
output.milestone_type = milestoneType;
output.outgrow-forms_id = template.form_id;
output.email_subject = template.subject;`,
        outputVars: [
          { name: "eligible", type: "boolean" },
          { name: "customer_email", type: "string" },
          { name: "milestone_type", type: "string" },
          { name: "outgrow-forms_id", type: "string" },
          { name: "email_subject", type: "string" },
        ],
      },
    }),
    emailNode(
      "3",
      "Email Event-Triggered Feedback Survey",
      520,
      "{customer_email}",
      "{email_subject}",
      "Hi,\\n\\nCongratulations on reaching a milestone with us! We would love to hear about your experience.\\n\\nPlease take 2 minutes to share your feedback:\\nhttps://yourcompany.outgrow-forms.com/{outgrow-forms_id}?email={customer_email}&milestone={milestone_type}\\n\\nYour input directly shapes how we improve our product and services.\\n\\nThank you!",
    ),
    n("4", "delayAction", "Wait 5 Days for Response", 760, {
      delayDetails: { duration: 5, unit: "days" },
    }),
    n("5", "runCode", "Collect Responses from Outgrow Forms API", 1000, {
      codeDetails: {
        code: `const email = inputs.customer_email || "";
const milestone = inputs.milestone_type || "general";
const formId = inputs.outgrow-forms_id || "";
// Response data from Outgrow Forms API
const responseCount = parseInt(inputs.response_count) || 0;
const overallScore = parseFloat(inputs.overall_score) || 0;
const feedback = inputs.feedback_text || "";
output.responded = responseCount > 0;
output.overall_score = overallScore;
output.feedback_text = feedback;
output.customer_email = email;
output.milestone_type = milestone;
output.form_id = formId;`,
        outputVars: [
          { name: "responded", type: "boolean" },
          { name: "overall_score", type: "number" },
          { name: "feedback_text", type: "string" },
          { name: "customer_email", type: "string" },
          { name: "milestone_type", type: "string" },
          { name: "form_id", type: "string" },
        ],
      },
    }),
    aiNode(
      "6",
      "AI Categorize Feedback Themes and Sentiment",
      1240,
      "You are a feedback analysis expert. Categorize the customer feedback into themes: product_quality, ease_of_use, support_experience, value_for_money, onboarding, feature_requests. Determine overall sentiment (positive, neutral, negative) and a confidence score (0-1). Generate a brief actionable summary. Output as JSON.",
      "Milestone: {milestone_type}, Score: {overall_score}, Feedback: {feedback_text}, Customer: {customer_email}",
      [
        { field: "themes", as: "feedback_themes", type: "string" },
        { field: "sentiment", as: "feedback_sentiment", type: "string" },
        { field: "confidence", as: "sentiment_confidence", type: "number" },
        { field: "summary", as: "actionable_summary", type: "string" },
      ],
    ),
    sheetsNode(
      "7",
      "Dashboard with Segmented Feedback Analysis",
      1480,
      "FeedbackAnalysis",
      '[["{customer_email}","{milestone_type}","{overall_score}","{feedback_sentiment}","{sentiment_confidence}","{feedback_themes}","{actionable_summary}","{responded}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Sends targeted feedback surveys at customer milestones (purchase, onboarding, renewal). Selects the appropriate Outgrow Forms template by milestone type, checks survey fatigue (60-day window), collects responses after 5 days, uses AI to categorize themes and sentiment, and logs segmented analysis to Google Sheets for a feedback dashboard.",
};

const customer_survey_distribution_bot = {
  nodes: [
    trigger("Manual: Marketing Team Launches Survey Campaign", "manual", {
      howItStarts:
        "Manually triggered by the marketing team when launching a new survey campaign.",
      setup:
        "Configure campaign parameters including target segments, survey URL, and personalization options.",
      inputFields: [
        {
          name: "campaign_name",
          type: "string",
          example: "Q1 2026 Product Satisfaction",
        },
        {
          name: "survey_url",
          type: "string",
          example: "https://yourcompany.outgrow-forms.com/q1-product-survey",
        },
        {
          name: "segment_criteria",
          type: "string",
          example: "enterprise,north_america,active_last_90_days",
        },
      ],
    }),
    n("2", "runCode", "Segment Audience by Tier, Product, Geography", 280, {
      codeDetails: {
        code: `const criteria = (inputs.segment_criteria || "").split(",");
const campaignName = inputs.campaign_name || "Survey Campaign";
const surveyUrl = inputs.survey_url || "";
const segments = {
  tiers: criteria.filter(c => ["enterprise","mid_market","smb"].includes(c)),
  products: criteria.filter(c => ["core","analytics","integrations","api"].includes(c)),
  geographies: criteria.filter(c => ["north_america","emea","apac","latam"].includes(c)),
};
const tierFilter = segments.tiers.length > 0 ? segments.tiers.join(",") : "all";
const geoFilter = segments.geographies.length > 0 ? segments.geographies.join(",") : "all";
output.campaign_name = campaignName;
output.survey_url = surveyUrl;
output.tier_filter = tierFilter;
output.geo_filter = geoFilter;
output.segment_description = "Tiers: " + tierFilter + ", Geo: " + geoFilter;
output.estimated_audience = 500;`,
        outputVars: [
          { name: "campaign_name", type: "string" },
          { name: "survey_url", type: "string" },
          { name: "tier_filter", type: "string" },
          { name: "geo_filter", type: "string" },
          { name: "segment_description", type: "string" },
          { name: "estimated_audience", type: "number" },
        ],
      },
    }),
    emailNode(
      "3",
      "Distribute Survey with Personalized Intro",
      520,
      "{customer_email}",
      "Your voice matters — {campaign_name}",
      "Hi {customer_name},\\n\\nAs a valued {tier_filter} customer, we are conducting our {campaign_name} and your feedback is especially important.\\n\\nThis survey takes about 3 minutes and will directly influence our product roadmap:\\n{survey_url}?email={customer_email}&segment={tier_filter}&geo={geo_filter}\\n\\nAs a thank you, all respondents will receive early access to our upcoming feature release.\\n\\nBest regards,\\nThe Product Team",
    ),
    n("4", "delayAction", "Wait 7 Days for Responses", 760, {
      delayDetails: { duration: 7, unit: "days" },
    }),
    n("5", "runCode", "Collect Responses and Compute Completion Rates", 1000, {
      codeDetails: {
        code: `const totalSent = parseInt(inputs.estimated_audience) || 500;
const totalResponses = parseInt(inputs.total_responses) || 0;
const completionRate = totalSent > 0 ? ((totalResponses / totalSent) * 100).toFixed(1) : "0.0";
const avgScore = parseFloat(inputs.avg_score) || 0;
output.total_sent = totalSent;
output.total_responses = totalResponses;
output.completion_rate = completionRate + "%";
output.avg_score = avgScore;
output.campaign_name = inputs.campaign_name || "";
output.segment_description = inputs.segment_description || "";`,
        outputVars: [
          { name: "total_sent", type: "number" },
          { name: "total_responses", type: "number" },
          { name: "completion_rate", type: "string" },
          { name: "avg_score", type: "number" },
          { name: "campaign_name", type: "string" },
          { name: "segment_description", type: "string" },
        ],
      },
    }),
    aiNode(
      "6",
      "AI Analyze Responses by Segment",
      1240,
      "You are a survey analytics expert. Analyze the survey responses segmented by customer tier and geography. Identify: top_insights (3 key findings), segment_differences (notable differences between segments), sentiment_breakdown (positive/neutral/negative percentages), and recommended_actions (2-3 specific follow-up actions). Output as JSON.",
      "Campaign: {campaign_name}, Segments: {segment_description}, Total Responses: {total_responses}, Completion Rate: {completion_rate}, Avg Score: {avg_score}",
      [
        { field: "top_insights", as: "top_insights", type: "string" },
        {
          field: "segment_differences",
          as: "segment_differences",
          type: "string",
        },
        {
          field: "sentiment_breakdown",
          as: "sentiment_breakdown",
          type: "string",
        },
        {
          field: "recommended_actions",
          as: "recommended_actions",
          type: "string",
        },
      ],
    ),
    slackNode(
      "7",
      "Slack Post Live Results Dashboard Link",
      1480,
      ":bar_chart: *Survey Campaign Results: {campaign_name}*\\n\\n*Overview:*\\n- Sent: {total_sent}\\n- Responses: {total_responses}\\n- Completion Rate: {completion_rate}\\n- Avg Score: {avg_score}\\n\\n*Top Insights:* {top_insights}\\n*Segment Differences:* {segment_differences}\\n*Sentiment:* {sentiment_breakdown}\\n*Recommended Actions:* {recommended_actions}",
    ),
    sheetsNode(
      "8",
      "Compile Campaign-Level Results with Segmentation",
      1720,
      "SurveyCampaigns",
      '[["{campaign_name}","{segment_description}","{total_sent}","{total_responses}","{completion_rate}","{avg_score}","{top_insights}","{segment_differences}","{recommended_actions}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Manages end-to-end survey campaign distribution. Marketing teams manually trigger campaigns with segmentation criteria, the system segments the audience by tier, product, and geography, distributes personalized survey emails, waits 7 days, collects completion rates, uses AI to analyze responses by segment, posts results to Slack, and compiles campaign-level data in Google Sheets.",
};

const nps_detractor_recovery_workflow = {
  nodes: [
    trigger(
      "Webhook: NPS Survey Response Received (Score 0-6)",
      "webhook_app",
      {
        source: "delighted",
        event: "response.created",
        howItStarts:
          "Fires when an NPS survey response is received with a detractor score (0-6) from Delighted.",
        setup:
          "Configure Delighted to send a webhook for new survey responses. This workflow filters for detractor scores (0-6).",
        inputFields: [],
      },
    ),
    n("2", "hitApi", "HubSpot Lookup Customer Profile", 280, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "results[0].properties.annualrevenue",
            customName: "customer_arr",
            type: "number",
          },
          {
            name: "results[0].properties.customer_tenure_months",
            customName: "tenure_months",
            type: "number",
          },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.hs_object_id",
            customName: "hubspot_id",
            type: "string",
          },
          {
            name: "results[0].properties.prior_support_tickets",
            customName: "prior_tickets",
            type: "number",
          },
        ],
      },
    }),
    aiNode(
      "3",
      "AI Analyze Detractor Feedback and Identify Root Cause",
      520,
      "You are a customer retention analyst. Analyze the NPS detractor feedback and identify: root_cause (product_issue, support_experience, pricing, missing_feature, reliability, onboarding), severity (low, medium, high, critical), recovery_priority (1-10 based on customer value and cause severity), and recommended_approach (specific recovery strategy). Output as JSON.",
      "NPS Score: {nps_score}, Verbatim feedback: {nps_feedback}, Customer ARR: ${customer_arr}, Tenure: {tenure_months} months, Prior tickets: {prior_tickets}",
      [
        { field: "root_cause", as: "root_cause", type: "string" },
        { field: "severity", as: "issue_severity", type: "string" },
        { field: "recovery_priority", as: "recovery_priority", type: "number" },
        {
          field: "recommended_approach",
          as: "recommended_approach",
          type: "string",
        },
      ],
    ),
    n("4", "runCode", "Select Recovery Strategy by Cause and Value", 760, {
      codeDetails: {
        code: `const arr = parseFloat(inputs.customer_arr) || 0;
const rootCause = inputs.root_cause || "unknown";
const severity = inputs.issue_severity || "medium";
const priority = parseInt(inputs.recovery_priority) || 5;
const isHighValue = arr > 10000;
const strategies = {
  product_issue: { action: "Schedule product walkthrough and share roadmap update", offer: "Extended premium support" },
  support_experience: { action: "CSM personal apology call and assign dedicated agent", offer: "Priority support queue for 90 days" },
  pricing: { action: "Account review with custom pricing proposal", offer: "Loyalty discount or annual commitment benefit" },
  missing_feature: { action: "Product team review and private beta invitation", offer: "Early access to upcoming features" },
  reliability: { action: "Engineering incident report and SLA credit", offer: "SLA credit and dedicated status page" },
  onboarding: { action: "Re-onboarding session with success manager", offer: "Complimentary onboarding extension" },
};
const strategy = strategies[rootCause] || strategies.product_issue;
output.recovery_action = strategy.action;
output.recovery_offer = strategy.offer;
output.is_high_value = isHighValue;
output.customer_arr = arr;
output.root_cause = rootCause;
output.severity = severity;`,
        outputVars: [
          { name: "recovery_action", type: "string" },
          { name: "recovery_offer", type: "string" },
          { name: "is_high_value", type: "boolean" },
          { name: "customer_arr", type: "number" },
          { name: "root_cause", type: "string" },
          { name: "severity", type: "string" },
        ],
      },
    }),
    n("5", "condition", "High-Value Customer (ARR > $10K)?", 1000, {
      conditionDetails: {
        conditions: [
          { variable: "{is_high_value}", operator: "equals", value: "true" },
        ],
      },
    }),
    n(
      "6",
      "approvalGate",
      "CSM Approves Recovery Action for High-Value",
      1240,
      {
        approvalDetails: {
          approver: "customer-success-manager",
          timeout: 24,
          timeoutUnit: "hours",
        },
      },
    ),
    emailNode(
      "7",
      "Email Personalized Recovery Outreach",
      1480,
      "{customer_email}",
      "We heard you, {first_name} — let us make it right",
      "Hi {first_name},\\n\\nThank you for sharing your honest feedback in our recent survey. We take your experience seriously and want to address your concerns directly.\\n\\nWe understand the issue: {root_cause}\\n\\nHere is what we are doing about it:\\n{recovery_action}\\n\\nAdditionally, we would like to offer you: {recovery_offer}\\n\\nI would love to schedule a call to discuss this further and ensure we are meeting your needs. Please reply to this email or book time at your convenience.\\n\\nBest regards,\\nYour Customer Success Team",
    ),
    sheetsNode(
      "8",
      "Track Recovery Outcomes",
      1720,
      "NPSRecovery",
      '[["{hubspot_id}","{customer_email}","{customer_arr}","{nps_score}","{root_cause}","{issue_severity}","{recovery_action}","{recovery_offer}","{is_high_value}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("5", "7", "output_2"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Recovers NPS detractors with a structured, value-based approach. When a detractor score (0-6) arrives, looks up the customer in HubSpot, AI identifies the root cause, selects a recovery strategy by cause and customer value. High-value customers (ARR >$10K) require CSM approval before outreach. Sends a personalized recovery email and tracks outcomes in Google Sheets.",
};

const nps_passive_education_nurture = {
  nodes: [
    trigger("Webhook: NPS Response with Score 7-8 (Passive)", "webhook_app", {
      source: "delighted",
      event: "response.created",
      howItStarts:
        "Fires when an NPS survey response is received with a passive score (7-8) from Delighted.",
      setup:
        "Configure Delighted to send a webhook for new survey responses. This workflow filters for passive scores (7-8).",
      inputFields: [],
    }),
    n("2", "hitApi", "HubSpot Fetch Customer Profile and Product Usage", 280, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.product_plan",
            customName: "product_plan",
            type: "string",
          },
          {
            name: "results[0].properties.features_used",
            customName: "features_used",
            type: "string",
          },
          {
            name: "results[0].properties.total_features_available",
            customName: "total_features",
            type: "number",
          },
          {
            name: "results[0].properties.last_login_date",
            customName: "last_login",
            type: "string",
          },
        ],
      },
    }),
    aiNode(
      "3",
      "AI Identify Education Opportunities from Underused Features",
      520,
      "You are a customer success expert focused on product adoption. Given the customer's current feature usage and available features, identify 3 underused features that would deliver the most value. For each, provide: feature_name, value_proposition (1 sentence on why it matters), and content_type (video_tutorial, guide, webinar, case_study). Also output an overall_adoption_score (0-100). Output as JSON.",
      "Customer plan: {product_plan}, Features used: {features_used}, Total available: {total_features}, NPS feedback: {nps_feedback}",
      [
        {
          field: "underused_features",
          as: "underused_features",
          type: "string",
        },
        { field: "adoption_score", as: "adoption_score", type: "number" },
        { field: "top_feature_name", as: "top_feature_name", type: "string" },
        { field: "top_feature_value", as: "top_feature_value", type: "string" },
      ],
    ),
    n("4", "runCode", "Select Nurture Content Sequence", 760, {
      codeDetails: {
        code: `const features = inputs.underused_features || "[]";
const adoptionScore = parseInt(inputs.adoption_score) || 50;
const topFeature = inputs.top_feature_name || "Advanced Analytics";
const topValue = inputs.top_feature_value || "Unlock deeper insights into your data";
const plan = inputs.product_plan || "standard";
const contentMap = {
  video_tutorial: "https://academy.yourcompany.com/tutorials/",
  guide: "https://docs.yourcompany.com/guides/",
  webinar: "https://yourcompany.com/webinars/upcoming",
  case_study: "https://yourcompany.com/customers/stories/",
};
output.nurture_content_url = contentMap.guide + topFeature.toLowerCase().replace(/ /g, "-");
output.top_feature = topFeature;
output.top_value = topValue;
output.adoption_score = adoptionScore;
output.customer_email = inputs.customer_email || "";
output.first_name = inputs.first_name || "";
output.content_sequence = "Week 1: Feature guide, Week 2: Video tutorial, Week 3: Webinar invite";`,
        outputVars: [
          { name: "nurture_content_url", type: "string" },
          { name: "top_feature", type: "string" },
          { name: "top_value", type: "string" },
          { name: "adoption_score", type: "number" },
          { name: "customer_email", type: "string" },
          { name: "first_name", type: "string" },
          { name: "content_sequence", type: "string" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email Targeted Educational Content",
      1000,
      "{customer_email}",
      "{first_name}, unlock more value with {top_feature}",
      "Hi {first_name},\\n\\nThank you for your recent feedback. We noticed you might benefit from exploring {top_feature} — {top_value}.\\n\\nHere is a quick guide to get started:\\n{nurture_content_url}\\n\\nCustomers who use this feature report significantly improved results. We think you will find it valuable too.\\n\\nQuestions? Reply to this email and our team will help you get set up.\\n\\nBest regards,\\nCustomer Success Team",
    ),
    n("6", "delayAction", "Wait 14 Days to Measure Impact", 1240, {
      delayDetails: { duration: 14, unit: "days" },
    }),
    sheetsNode(
      "7",
      "Track Engagement and NPS Improvement",
      1480,
      "NPSPassiveNurture",
      '[["{customer_email}","{first_name}","{product_plan}","{adoption_score}","{top_feature}","{nurture_content_url}","{content_sequence}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Nurtures NPS passive respondents (7-8) toward promoter status through targeted education. Fetches customer profile and product usage from HubSpot, uses AI to identify underused high-value features, selects appropriate educational content, sends a personalized email with feature guides, waits 14 days, and tracks engagement and NPS improvement over time.",
};

const nps_survey_after_milestone = {
  nodes: [
    trigger("Webhook: Customer Reaches Milestone", "webhook_app", {
      source: "product_platform",
      event: "customer.milestone_reached",
      howItStarts:
        "Fires when a customer reaches a defined milestone: 30-day mark, 90-day mark, or renewal date.",
      setup:
        "Configure your product platform to send webhooks at customer milestones (30-day, 90-day, renewal).",
      inputFields: [],
    }),
    n(
      "2",
      "runCode",
      "Verify Milestone Relevance and Survey Eligibility",
      280,
      {
        codeDetails: {
          code: `const email = inputs.customer_email || "";
const milestone = inputs.milestone_type || "30_day";
const lastNps = inputs.last_nps_date || "";
const now = Date.now();
const meaningfulMilestones = ["30_day", "90_day", "renewal", "anniversary"];
const isMeaningful = meaningfulMilestones.includes(milestone);
const notRecentlySurveyed = !lastNps || (now - new Date(lastNps).getTime()) > (60 * 24 * 60 * 60 * 1000);
output.eligible = isMeaningful && notRecentlySurveyed;
output.customer_email = email;
output.milestone_type = milestone;
output.milestone_label = milestone.replace(/_/g, " ").replace(/\\b\\w/g, l => l.toUpperCase());`,
          outputVars: [
            { name: "eligible", type: "boolean" },
            { name: "customer_email", type: "string" },
            { name: "milestone_type", type: "string" },
            { name: "milestone_label", type: "string" },
          ],
        },
      },
    ),
    n(
      "3",
      "condition",
      "Meaningful Milestone and Not Recently Surveyed?",
      520,
      {
        conditionDetails: {
          conditions: [
            { variable: "{eligible}", operator: "equals", value: "true" },
          ],
        },
      },
    ),
    n("4", "hitApi", "Email NPS Survey via Delighted API", 760, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.delighted.com/v1/people.json",
        headers: [
          { key: "Authorization", value: "Basic {delighted_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "email", value: "{customer_email}" },
          { key: "delay", value: "0" },
          { key: "properties[milestone]", value: "{milestone_type}" },
          { key: "properties[source]", value: "milestone_trigger" },
        ],
        variables: [
          { name: "id", customName: "delighted_person_id", type: "string" },
          { name: "email", customName: "survey_email", type: "string" },
          { name: "created_at", customName: "survey_sent_at", type: "number" },
        ],
      },
    }),
    n("5", "delayAction", "Wait 5 Days for NPS Response", 1000, {
      delayDetails: { duration: 5, unit: "days" },
    }),
    n("6", "runCode", "Collect NPS Score and Verbatim", 1240, {
      codeDetails: {
        code: `const personId = inputs.delighted_person_id || "";
const npsScore = parseInt(inputs.nps_score) || -1;
const verbatim = inputs.nps_verbatim || "";
const responded = npsScore >= 0;
const category = npsScore >= 9 ? "promoter" : (npsScore >= 7 ? "passive" : "detractor");
output.responded = responded;
output.nps_score = npsScore;
output.nps_verbatim = verbatim;
output.nps_category = category;
output.delighted_person_id = personId;
output.customer_email = inputs.customer_email || "";
output.milestone_type = inputs.milestone_type || "";`,
        outputVars: [
          { name: "responded", type: "boolean" },
          { name: "nps_score", type: "number" },
          { name: "nps_verbatim", type: "string" },
          { name: "nps_category", type: "string" },
          { name: "delighted_person_id", type: "string" },
          { name: "customer_email", type: "string" },
          { name: "milestone_type", type: "string" },
        ],
      },
    }),
    sheetsNode(
      "7",
      "Log NPS with Milestone Context",
      1480,
      "NPSMilestone",
      '[["{customer_email}","{milestone_type}","{nps_score}","{nps_category}","{nps_verbatim}","{responded}","{delighted_person_id}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4", "output_1"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Sends NPS surveys via Delighted when customers reach key milestones (30-day, 90-day, renewal). Verifies milestone relevance and checks a 60-day survey fatigue window, sends the survey through the Delighted API, waits 5 days, collects the NPS score and verbatim, categorizes as promoter/passive/detractor, and logs everything with milestone context.",
};

const nps_survey_bot = {
  nodes: [
    trigger("Webhook: Conversational NPS Trigger in Chat", "webhook_app", {
      source: "chat_platform",
      event: "nps_trigger_requested",
      howItStarts:
        "Fires when the conversational NPS survey is triggered during a chat session (e.g., after issue resolution or at a natural pause).",
      setup:
        "Configure your chat platform to fire a webhook when conditions are met for an in-chat NPS survey.",
      inputFields: [],
    }),
    n("2", "sendMessage", "Ask NPS Question: 0-10 Recommendation", 280, {
      sendMessageDetails: {
        message:
          "We value your opinion! On a scale of 0 to 10, how likely are you to recommend us to a friend or colleague?\\n\\nJust type a number from 0 (not at all likely) to 10 (extremely likely).",
      },
    }),
    aiNode(
      "3",
      "AI Parse Response and Extract Numeric Score",
      520,
      "You are an NPS response parser. The customer has responded to an NPS question (0-10 scale). Extract the numeric score from their response, handling various formats: just a number, text like 'eight' or '8 out of 10', or embedded in a sentence. If no valid score is found, set score to -1 and valid to false. Output: score (integer 0-10 or -1), valid (true/false), customer_sentiment (brief interpretation).",
      "Parse this NPS response for a 0-10 score: {customer_response}",
      [
        { field: "score", as: "nps_score", type: "number" },
        { field: "valid", as: "score_valid", type: "string" },
        {
          field: "customer_sentiment",
          as: "initial_sentiment",
          type: "string",
        },
      ],
    ),
    n("4", "condition", "Valid Score Provided?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{score_valid}", operator: "equals", value: "true" },
        ],
      },
    }),
    n("5", "sendMessage", "Adaptive Follow-Up Question Based on Score", 1000, {
      sendMessageDetails: {
        message:
          "Thank you for the score of {nps_score}! We appreciate your honesty.\\n\\nCould you share a bit more about what influenced your rating? What could we do to improve your experience?",
      },
    }),
    aiNode(
      "6",
      "AI Analyze Verbatim Feedback",
      1240,
      "You are a customer feedback analyst. Analyze the NPS verbatim response along with the numeric score. Identify: themes (product, support, pricing, features, reliability), key_feedback (the core message in 1-2 sentences), improvement_area (the single most impactful area to improve), and emotional_tone (enthusiastic, satisfied, neutral, frustrated, angry). Output as JSON.",
      "NPS Score: {nps_score}, Follow-up response: {followup_response}, Initial sentiment: {initial_sentiment}",
      [
        { field: "themes", as: "feedback_themes", type: "string" },
        { field: "key_feedback", as: "key_feedback", type: "string" },
        { field: "improvement_area", as: "improvement_area", type: "string" },
        { field: "emotional_tone", as: "emotional_tone", type: "string" },
      ],
    ),
    n("7", "runCode", "Categorize as Promoter/Passive/Detractor", 1480, {
      codeDetails: {
        code: `const score = parseInt(inputs.nps_score) || 0;
const category = score >= 9 ? "promoter" : (score >= 7 ? "passive" : "detractor");
const email = inputs.customer_email || "";
const chatId = inputs.chat_id || "";
output.nps_category = category;
output.nps_score = score;
output.customer_email = email;
output.chat_id = chatId;
output.themes = inputs.feedback_themes || "";
output.key_feedback = inputs.key_feedback || "";
output.improvement_area = inputs.improvement_area || "";
output.emotional_tone = inputs.emotional_tone || "";`,
        outputVars: [
          { name: "nps_category", type: "string" },
          { name: "nps_score", type: "number" },
          { name: "customer_email", type: "string" },
          { name: "chat_id", type: "string" },
          { name: "themes", type: "string" },
          { name: "key_feedback", type: "string" },
          { name: "improvement_area", type: "string" },
          { name: "emotional_tone", type: "string" },
        ],
      },
    }),
    sheetsNode(
      "8",
      "Log Conversational NPS Survey Data",
      1720,
      "NPSConversational",
      '[["{chat_id}","{customer_email}","{nps_score}","{nps_category}","{emotional_tone}","{feedback_themes}","{key_feedback}","{improvement_area}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Conducts conversational NPS surveys within a chat session. Asks the NPS question (0-10), uses AI to parse numeric scores from natural language, adapts the follow-up question based on the score range, analyzes verbatim feedback for themes and emotional tone, categorizes as promoter/passive/detractor, and logs the complete conversational survey data.",
};

const service_appointment_follow_up = {
  nodes: [
    trigger("Webhook: Service Appointment Completed", "webhook_app", {
      source: "scheduling_platform",
      event: "appointment.completed",
      howItStarts:
        "Fires when a service appointment is marked as completed in the scheduling system.",
      setup:
        "Configure your scheduling platform (Calendly, Acuity, ServiceTitan) to send a webhook when an appointment is completed.",
      inputFields: [],
    }),
    n("2", "runCode", "Fetch Appointment Details", 280, {
      codeDetails: {
        code: `const appointmentId = inputs.appointment_id || "";
const customerEmail = inputs.customer_email || "";
const customerName = inputs.customer_name || "Customer";
const technicianName = inputs.technician_name || "Technician";
const serviceType = inputs.service_type || "general";
const resolutionStatus = inputs.resolution_status || "completed";
const appointmentDate = inputs.appointment_date || new Date().toISOString().split("T")[0];
output.appointment_id = appointmentId;
output.customer_email = customerEmail;
output.customer_name = customerName;
output.technician_name = technicianName;
output.service_type = serviceType;
output.resolution_status = resolutionStatus;
output.appointment_date = appointmentDate;`,
        outputVars: [
          { name: "appointment_id", type: "string" },
          { name: "customer_email", type: "string" },
          { name: "customer_name", type: "string" },
          { name: "technician_name", type: "string" },
          { name: "service_type", type: "string" },
          { name: "resolution_status", type: "string" },
          { name: "appointment_date", type: "string" },
        ],
      },
    }),
    n("3", "delayAction", "Wait 4 Hours After Appointment", 520, {
      delayDetails: { duration: 4, unit: "hours" },
    }),
    emailNode(
      "4",
      "Email Follow-Up Survey Tied to Service",
      760,
      "{customer_email}",
      "How was your {service_type} appointment with {technician_name}?",
      "Hi {customer_name},\\n\\nThank you for your {service_type} appointment on {appointment_date}. We hope everything went well!\\n\\nPlease take a moment to rate your experience with {technician_name}:\\n\\n{survey_url}/service-feedback?appt={appointment_id}&tech={technician_name}\\n\\nYour feedback helps us maintain high service quality and recognize outstanding work.\\n\\nThank you!",
    ),
    aiNode(
      "5",
      "AI Analyze Feedback for Service Quality Themes",
      1000,
      "You are a service quality analyst. Analyze the post-appointment feedback including ratings and comments. Identify: overall_score (1-5), service_quality_themes (punctuality, professionalism, technical_skill, communication, cleanliness), is_negative (true if score <= 2 or negative sentiment), and summary (brief actionable summary). Output as JSON.",
      "Appointment: {appointment_id}, Technician: {technician_name}, Service: {service_type}, Rating: {service_rating}, Comments: {service_feedback}",
      [
        { field: "overall_score", as: "service_score", type: "number" },
        {
          field: "service_quality_themes",
          as: "quality_themes",
          type: "string",
        },
        { field: "is_negative", as: "is_negative_feedback", type: "string" },
        { field: "summary", as: "feedback_summary", type: "string" },
      ],
    ),
    n("6", "condition", "Negative Feedback Received?", 1240, {
      conditionDetails: {
        conditions: [
          {
            variable: "{is_negative_feedback}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    slackNode(
      "7",
      "Slack Alert Service Manager with Context",
      1480,
      ":warning: *Negative Service Feedback Alert*\\n*Appointment:* {appointment_id}\\n*Customer:* {customer_name} ({customer_email})\\n*Technician:* {technician_name}\\n*Service Type:* {service_type}\\n*Score:* {service_score}/5\\n*Quality Themes:* {quality_themes}\\n*Summary:* {feedback_summary}\\nPlease follow up with the technician and customer.",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7", "output_1"),
  ],
  summary:
    "Follows up on service appointments with targeted surveys. Waits 4 hours after appointment completion, sends a follow-up survey tied to the specific technician and service type, uses AI to analyze feedback for quality themes, and alerts the service manager in Slack when negative feedback is detected for immediate follow-up.",
};

const webengage_feedback_collection_flow = {
  nodes: [
    trigger(
      "Webhook: WebEngage Journey Triggers Feedback Request",
      "webhook_app",
      {
        source: "webengage",
        event: "journey.feedback_trigger",
        howItStarts:
          "Fires when a WebEngage customer journey reaches a feedback collection node.",
        setup:
          "Configure a WebEngage journey to send a webhook when a customer reaches the feedback collection stage.",
        inputFields: [],
      },
    ),
    n("2", "runCode", "Determine Survey Type by Journey Stage", 280, {
      codeDetails: {
        code: `const journeyName = inputs.journey_name || "general";
const journeyStage = inputs.journey_stage || "mid";
const email = inputs.customer_email || "";
const userId = inputs.user_id || "";
const stageMap = {
  onboarding: { surveyType: "onboarding_experience", questionCount: 5 },
  activation: { surveyType: "feature_adoption", questionCount: 4 },
  engagement: { surveyType: "product_satisfaction", questionCount: 6 },
  retention: { surveyType: "loyalty_nps", questionCount: 3 },
  expansion: { surveyType: "upsell_readiness", questionCount: 4 },
};
const config = stageMap[journeyStage] || stageMap.engagement;
output.survey_type = config.surveyType;
output.question_count = config.questionCount;
output.customer_email = email;
output.user_id = userId;
output.journey_name = journeyName;
output.journey_stage = journeyStage;`,
        outputVars: [
          { name: "survey_type", type: "string" },
          { name: "question_count", type: "number" },
          { name: "customer_email", type: "string" },
          { name: "user_id", type: "string" },
          { name: "journey_name", type: "string" },
          { name: "journey_stage", type: "string" },
        ],
      },
    }),
    n("3", "hitApi", "Send Survey via WebEngage Engagement API", 520, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.webengage.com/v2/accounts/{webengage_account_id}/events",
        headers: [
          { key: "Authorization", value: "Bearer {webengage_api_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "userId", value: "{user_id}" },
          { key: "eventName", value: "survey_triggered" },
          {
            key: "eventData",
            value:
              '{"survey_type":"{survey_type}","journey":"{journey_name}","stage":"{journey_stage}"}',
          },
        ],
        variables: [
          {
            name: "response.status",
            customName: "engagement_status",
            type: "string",
          },
          { name: "response.eventId", customName: "event_id", type: "string" },
        ],
      },
    }),
    n("4", "delayAction", "Wait 48 Hours for Response", 760, {
      delayDetails: { duration: 48, unit: "hours" },
    }),
    n("5", "runCode", "Collect Response Data from WebEngage", 1000, {
      codeDetails: {
        code: `const userId = inputs.user_id || "";
const surveyType = inputs.survey_type || "";
const eventId = inputs.event_id || "";
const score = parseFloat(inputs.survey_score) || 0;
const feedback = inputs.survey_feedback || "";
const responded = score > 0;
output.responded = responded;
output.survey_score = score;
output.survey_feedback = feedback;
output.user_id = userId;
output.survey_type = surveyType;
output.event_id = eventId;
output.journey_name = inputs.journey_name || "";
output.journey_stage = inputs.journey_stage || "";
output.customer_email = inputs.customer_email || "";`,
        outputVars: [
          { name: "responded", type: "boolean" },
          { name: "survey_score", type: "number" },
          { name: "survey_feedback", type: "string" },
          { name: "user_id", type: "string" },
          { name: "survey_type", type: "string" },
          { name: "event_id", type: "string" },
          { name: "journey_name", type: "string" },
          { name: "journey_stage", type: "string" },
          { name: "customer_email", type: "string" },
        ],
      },
    }),
    aiNode(
      "6",
      "AI Analyze Feedback Themes",
      1240,
      "You are a journey analytics expert. Analyze the survey feedback from a WebEngage journey. Identify: themes (onboarding, features, support, pricing, ux), sentiment (positive/neutral/negative), journey_health (on_track, at_risk, needs_intervention), and actionable_insight (one specific recommendation). Output as JSON.",
      "Journey: {journey_name}, Stage: {journey_stage}, Survey Type: {survey_type}, Score: {survey_score}, Feedback: {survey_feedback}",
      [
        { field: "themes", as: "feedback_themes", type: "string" },
        { field: "sentiment", as: "feedback_sentiment", type: "string" },
        { field: "journey_health", as: "journey_health", type: "string" },
        {
          field: "actionable_insight",
          as: "actionable_insight",
          type: "string",
        },
      ],
    ),
    sheetsNode(
      "7",
      "Aggregate with Journey Attribution",
      1480,
      "WebEngageFeedback",
      '[["{user_id}","{customer_email}","{journey_name}","{journey_stage}","{survey_type}","{survey_score}","{feedback_sentiment}","{journey_health}","{feedback_themes}","{actionable_insight}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Collects feedback at specific points in WebEngage customer journeys. Determines the appropriate survey type by journey stage, sends surveys through the WebEngage API, waits 48 hours for responses, uses AI to analyze themes and assess journey health, and aggregates results with full journey attribution in Google Sheets.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * S4 — RECOVERY / CHURN — FIRST 10 WORKFLOWS
 * ─────────────────────────────────────────────────────────────────────────── */

const cancellation_prevention_bot = {
  nodes: [
    trigger("Webhook: Customer Initiates Cancellation", "webhook_app", {
      source: "billing_platform",
      event: "subscription.cancellation_initiated",
      howItStarts:
        "Fires when a customer initiates a subscription cancellation through the billing portal or support channel.",
      setup:
        "Configure your billing platform (Stripe, Chargebee, Recurly) to send a webhook when a cancellation is initiated.",
      inputFields: [],
    }),
    n("2", "hitApi", "HubSpot Fetch Customer Profile and History", 280, {
      hitApiDetails: {
        method: "GET",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: [
          {
            name: "results[0].properties.annualrevenue",
            customName: "customer_arr",
            type: "number",
          },
          {
            name: "results[0].properties.customer_tenure_months",
            customName: "tenure_months",
            type: "number",
          },
          {
            name: "results[0].properties.email",
            customName: "customer_email",
            type: "string",
          },
          {
            name: "results[0].properties.firstname",
            customName: "first_name",
            type: "string",
          },
          {
            name: "results[0].properties.product_plan",
            customName: "current_plan",
            type: "string",
          },
          {
            name: "results[0].properties.satisfaction_score",
            customName: "satisfaction_score",
            type: "number",
          },
          {
            name: "results[0].properties.feature_usage_pct",
            customName: "usage_pct",
            type: "number",
          },
        ],
      },
    }),
    aiNode(
      "3",
      "AI Analyze Cancellation Reason and Select Strategy",
      520,
      "You are a customer retention expert. Analyze the cancellation reason, customer profile, and usage data to determine: cancellation_category (pricing, missing_feature, competitor, poor_experience, not_using, downsizing, other), retention_strategy (discount, upgrade, training, feature_preview, executive_call, pause_offer), win_back_probability (0-100), and talking_points (3 specific points to address their concern). Output as JSON.",
      "Cancellation reason: {cancellation_reason}, ARR: ${customer_arr}, Tenure: {tenure_months} months, Plan: {current_plan}, Usage: {usage_pct}%, Satisfaction: {satisfaction_score}, Customer: {first_name}",
      [
        {
          field: "cancellation_category",
          as: "cancel_category",
          type: "string",
        },
        {
          field: "retention_strategy",
          as: "retention_strategy",
          type: "string",
        },
        { field: "win_back_probability", as: "win_back_prob", type: "number" },
        { field: "talking_points", as: "talking_points", type: "string" },
      ],
    ),
    n("4", "runCode", "Determine Retention Offer by Value and Reason", 760, {
      codeDetails: {
        code: `const arr = parseFloat(inputs.customer_arr) || 0;
const category = inputs.cancel_category || "other";
const strategy = inputs.retention_strategy || "discount";
const winBackProb = parseInt(inputs.win_back_prob) || 0;
const isHighValue = arr > 10000;
const offerMap = {
  pricing: { offer: "20% discount for 6 months on current plan", escalation: "Custom pricing review with finance" },
  missing_feature: { offer: "Early access to upcoming feature + product roadmap review", escalation: "Product team meeting to discuss requirements" },
  competitor: { offer: "Competitive match pricing + migration support credit", escalation: "Executive call to discuss strategic partnership" },
  poor_experience: { offer: "Dedicated account manager for 90 days + priority support", escalation: "VP of CS personal outreach and remediation plan" },
  not_using: { offer: "Free re-onboarding with dedicated trainer", escalation: "Custom success plan with quarterly business reviews" },
  downsizing: { offer: "Pause subscription for up to 3 months", escalation: "Flexible plan adjustment with annual commitment" },
  other: { offer: "30-day free extension to resolve concerns", escalation: "CSM personal consultation call" },
};
const offerConfig = offerMap[category] || offerMap.other;
output.retention_offer = isHighValue ? offerConfig.escalation : offerConfig.offer;
output.is_high_value = isHighValue;
output.customer_arr = arr;
output.cancel_category = category;
output.win_back_prob = winBackProb;
output.customer_email = inputs.customer_email || "";
output.first_name = inputs.first_name || "";
output.talking_points = inputs.talking_points || "";`,
        outputVars: [
          { name: "retention_offer", type: "string" },
          { name: "is_high_value", type: "boolean" },
          { name: "customer_arr", type: "number" },
          { name: "cancel_category", type: "string" },
          { name: "win_back_prob", type: "number" },
          { name: "customer_email", type: "string" },
          { name: "first_name", type: "string" },
          { name: "talking_points", type: "string" },
        ],
      },
    }),
    n("5", "condition", "High-Value Customer (ARR > $10K)?", 1000, {
      conditionDetails: {
        conditions: [
          { variable: "{is_high_value}", operator: "equals", value: "true" },
        ],
      },
    }),
    n("6", "approvalGate", "CSM Approves Retention Offer", 1240, {
      approvalDetails: {
        approver: "customer-success-manager",
        timeout: 12,
        timeoutUnit: "hours",
      },
    }),
    n(
      "7",
      "sendMessage",
      "Present Retention Offer with Value Proposition",
      1480,
      {
        sendMessageDetails: {
          message:
            "Hi {first_name}, we noticed you are considering cancelling and we want to help.\\n\\nWe understand your concern about {cancel_category}. Here is what we can do:\\n\\n{retention_offer}\\n\\nKey points to consider:\\n{talking_points}\\n\\nWould you like to discuss this further? We are committed to making this work for you.",
        },
      },
    ),
    n("8", "condition", "Customer Accepts Retention Offer?", 1720, {
      conditionDetails: {
        conditions: [
          { variable: "{offer_accepted}", operator: "equals", value: "true" },
        ],
      },
    }),
    sheetsNode(
      "9",
      "Log Cancellation Outcome and Offer Effectiveness",
      1960,
      "CancellationPrevention",
      '[["{customer_email}","{first_name}","{customer_arr}","{cancel_category}","{retention_strategy}","{retention_offer}","{win_back_prob}","{is_high_value}","{offer_accepted}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("5", "7", "output_2"),
    e("6", "7"),
    e("7", "8"),
    e("8", "9", "output_1"),
    e("8", "9", "output_2"),
  ],
  summary:
    "Intercepts cancellation attempts with a structured retention process. Fetches customer profile from HubSpot, AI analyzes the cancellation reason and selects a retention strategy, determines offers by customer value. High-value customers require CSM approval. Presents a personalized retention offer with specific talking points and tracks acceptance rates and offer effectiveness.",
};

const concierge_chat_follow_up = {
  nodes: [
    trigger("Webhook: VIP/Concierge Chat Session Ends", "webhook_app", {
      source: "chat_platform",
      event: "vip_chat.ended",
      howItStarts:
        "Fires when a VIP or concierge-level chat session is completed.",
      setup:
        "Configure your chat platform to send a webhook when a VIP/concierge-tagged chat session ends.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Summarize Conversation with Action Items",
      280,
      "You are a concierge service manager. Analyze the VIP chat transcript and generate: summary (3-5 sentence conversation summary), action_items (numbered list of specific follow-up tasks), promises_made (any commitments or timelines given to the customer), customer_email, customer_name, and urgency (low/medium/high based on commitments). Output as JSON.",
      "Summarize this VIP chat session and extract action items: {webhook_payload}",
      [
        { field: "summary", as: "chat_summary", type: "string" },
        { field: "action_items", as: "action_items", type: "string" },
        { field: "promises_made", as: "promises_made", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "customer_name", as: "customer_name", type: "string" },
        { field: "urgency", as: "follow_up_urgency", type: "string" },
      ],
    ),
    n("3", "hitApi", "Jira Create Follow-Up Tasks from Action Items", 520, {
      hitApiDetails: {
        method: "POST",
        url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
        headers: [
          { key: "Authorization", value: "Basic {jira_api_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "fields",
            value:
              '{"project":{"key":"CS"},"summary":"VIP Follow-Up: {customer_name} — Action Items","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Customer: {customer_name} ({customer_email})\\nUrgency: {follow_up_urgency}\\n\\nChat Summary:\\n{chat_summary}\\n\\nAction Items:\\n{action_items}\\n\\nPromises Made:\\n{promises_made}"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"High"},"labels":["vip-followup","concierge"]}',
          },
        ],
        variables: [
          { name: "id", customName: "jira_task_id", type: "string" },
          { name: "key", customName: "jira_task_key", type: "string" },
        ],
      },
    }),
    emailNode(
      "4",
      "Email Summary to Customer with Next Steps",
      760,
      "{customer_email}",
      "Summary of your concierge session — next steps",
      "Hi {customer_name},\\n\\nThank you for your time today. Here is a summary of our conversation:\\n\\n{chat_summary}\\n\\nAction items we are working on:\\n{action_items}\\n\\nCommitments and timeline:\\n{promises_made}\\n\\nWe will keep you updated on progress. If you need anything in the meantime, reply to this email.\\n\\nBest regards,\\nYour Concierge Team",
    ),
    n("5", "delayAction", "Wait 24 Hours to Check Task Progress", 1000, {
      delayDetails: { duration: 24, unit: "hours" },
    }),
    n("6", "runCode", "Check Task Completion Status", 1240, {
      codeDetails: {
        code: `const taskKey = inputs.jira_task_key || "";
const taskStatus = inputs.task_status || "open";
const promisesMade = inputs.promises_made || "";
const customerName = inputs.customer_name || "";
const completedTasks = parseInt(inputs.completed_count) || 0;
const totalTasks = parseInt(inputs.total_count) || 1;
const allComplete = completedTasks >= totalTasks;
const completionPct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
output.all_complete = allComplete;
output.completion_pct = completionPct;
output.task_key = taskKey;
output.customer_name = customerName;
output.promises_made = promisesMade;
output.overdue = !allComplete;`,
        outputVars: [
          { name: "all_complete", type: "boolean" },
          { name: "completion_pct", type: "number" },
          { name: "task_key", type: "string" },
          { name: "customer_name", type: "string" },
          { name: "promises_made", type: "string" },
          { name: "overdue", type: "boolean" },
        ],
      },
    }),
    slackNode(
      "7",
      "Slack Alert Concierge if Promises Unfulfilled",
      1480,
      ":rotating_light: *VIP Follow-Up Alert: Unfulfilled Promises*\\n*Customer:* {customer_name}\\n*Jira Task:* {task_key}\\n*Completion:* {completion_pct}%\\n*Promises Made:* {promises_made}\\n\\nAction items are overdue. Please prioritize completion to maintain VIP service standards.",
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Ensures follow-through on VIP concierge chat commitments. AI summarizes the conversation and extracts action items, creates Jira tasks, emails the customer a summary with next steps and timeline, then after 24 hours checks task completion and alerts the concierge team in Slack if any promises remain unfulfilled.",
};

const customer_advisory_board_invite = {
  nodes: [
    trigger("Schedule: Quarterly CAB Candidate Selection", "schedule", {
      schedule: "0 9 1 1,4,7,10 *",
      howItStarts:
        "Runs quarterly (Jan, Apr, Jul, Oct) on the 1st at 9 AM to identify Customer Advisory Board candidates.",
      setup:
        "No external setup needed. The schedule runs quarterly for CAB candidate identification.",
      inputFields: [],
    }),
    n("2", "hitApi", "HubSpot Fetch CAB-Eligible Customers", 280, {
      hitApiDetails: {
        method: "POST",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "filterGroups",
            value:
              '[{"filters":[{"propertyName":"nps_score","operator":"GTE","value":"9"},{"propertyName":"customer_engagement_score","operator":"GTE","value":"70"},{"propertyName":"annualrevenue","operator":"GTE","value":"5000"}]}]',
          },
          {
            key: "properties",
            value:
              '["email","firstname","lastname","company","industry","annualrevenue","nps_score","customer_engagement_score"]',
          },
          { key: "limit", value: "50" },
        ],
        variables: [
          { name: "total", customName: "total_candidates", type: "number" },
          { name: "results", customName: "candidate_list", type: "string" },
        ],
      },
    }),
    aiNode(
      "3",
      "AI Score Candidates for Advisory Board Fit",
      520,
      "You are an executive program manager. Score each candidate for Customer Advisory Board fit based on: strategic value (industry representation, company size), engagement level (NPS, participation history), diversity of perspective (different industries, company sizes, use cases). Output: ranked_candidates (top 15 with scores and rationale), diversity_score (0-100 for overall board diversity), and selection_rationale. Output as JSON.",
      "Score these CAB candidates for fit: {candidate_list}. Total candidates: {total_candidates}",
      [
        { field: "ranked_candidates", as: "ranked_candidates", type: "string" },
        { field: "diversity_score", as: "diversity_score", type: "number" },
        {
          field: "selection_rationale",
          as: "selection_rationale",
          type: "string",
        },
      ],
    ),
    n("4", "runCode", "Rank and Select Top Candidates", 760, {
      codeDetails: {
        code: `const ranked = inputs.ranked_candidates || "[]";
const diversityScore = parseInt(inputs.diversity_score) || 0;
const rationale = inputs.selection_rationale || "";
// Select top 8 candidates for invitation
const maxInvites = 8;
output.invite_count = maxInvites;
output.ranked_candidates = ranked;
output.diversity_score = diversityScore;
output.selection_rationale = rationale;
output.quarter = "Q" + (Math.ceil((new Date().getMonth() + 1) / 3)) + " " + new Date().getFullYear();`,
        outputVars: [
          { name: "invite_count", type: "number" },
          { name: "ranked_candidates", type: "string" },
          { name: "diversity_score", type: "number" },
          { name: "selection_rationale", type: "string" },
          { name: "quarter", type: "string" },
        ],
      },
    }),
    n("5", "approvalGate", "Executive Approves Invite List", 1000, {
      approvalDetails: {
        approver: "vp-customer-success",
        timeout: 72,
        timeoutUnit: "hours",
      },
    }),
    emailNode(
      "6",
      "Email Personalized CAB Invitation",
      1240,
      "{candidate_email}",
      "Exclusive Invitation: Join Our Customer Advisory Board — {quarter}",
      "Dear {candidate_name},\\n\\nI am personally reaching out to invite you to join our Customer Advisory Board for {quarter}.\\n\\nAs one of our most valued customers, your insights and perspective are invaluable. The Advisory Board meets quarterly to:\\n\\n- Shape our product roadmap with direct input\\n- Preview upcoming features before release\\n- Network with other industry leaders\\n- Provide strategic guidance to our executive team\\n\\nBoard members receive exclusive early access to new features, priority support, and direct access to our leadership team.\\n\\nWould you be interested in joining? Please reply to this email or schedule a brief call to discuss: {calendar_link}\\n\\nBest regards,\\n{executive_name}\\nVP of Customer Success",
    ),
    sheetsNode(
      "7",
      "Track Acceptance and Participation",
      1480,
      "CABTracking",
      '[["{quarter}","{candidate_email}","{candidate_name}","{candidate_company}","{diversity_score}","{selection_rationale}","{invite_count}","invited","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Manages quarterly Customer Advisory Board member selection and invitation. Fetches high-NPS, high-engagement customers from HubSpot, AI scores candidates for advisory board fit considering diversity and strategic value, executive approval gates the invite list, sends personalized invitations with executive signature, and tracks acceptance rates and participation.",
};

const customer_communication_preference_center = {
  nodes: [
    trigger(
      "Webhook: Customer Updates Communication Preferences",
      "webhook_app",
      {
        source: "preference_center",
        event: "preferences.updated",
        howItStarts:
          "Fires when a customer updates their communication preferences through the preference center.",
        setup:
          "Connect your preference center to send a webhook when customers change their communication settings.",
        inputFields: [],
      },
    ),
    n(
      "2",
      "runCode",
      "Validate Consent Compliance by Channel and Jurisdiction",
      280,
      {
        codeDetails: {
          code: `const email = inputs.customer_email || "";
const country = inputs.customer_country || "US";
const channels = inputs.updated_channels || "";
const optIns = inputs.opt_ins || "";
const optOuts = inputs.opt_outs || "";
// GDPR requires explicit opt-in for EU; CAN-SPAM requires opt-out honor
const euCountries = ["DE","FR","IT","ES","NL","BE","AT","PT","IE","FI","SE","DK","PL","CZ","RO","HU","BG","HR","SK","SI","LT","LV","EE","CY","LU","MT","GR"];
const isEU = euCountries.includes(country);
const complianceFramework = isEU ? "GDPR" : (country === "CA" ? "CASL" : "CAN-SPAM");
const hasOptOuts = optOuts && optOuts.length > 0;
const hasCriticalOptOut = optOuts.includes("transactional") || optOuts.includes("security");
output.compliant = true;
output.compliance_framework = complianceFramework;
output.has_critical_opt_out = hasCriticalOptOut;
output.customer_email = email;
output.country = country;
output.opt_ins = optIns;
output.opt_outs = optOuts;
output.is_bulk_change = optOuts.split(",").length > 3;`,
          outputVars: [
            { name: "compliant", type: "boolean" },
            { name: "compliance_framework", type: "string" },
            { name: "has_critical_opt_out", type: "boolean" },
            { name: "customer_email", type: "string" },
            { name: "country", type: "string" },
            { name: "opt_ins", type: "string" },
            { name: "opt_outs", type: "string" },
            { name: "is_bulk_change", type: "boolean" },
          ],
        },
      },
    ),
    n("3", "hitApi", "HubSpot Update Contact Preferences", 520, {
      hitApiDetails: {
        method: "PATCH",
        url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          {
            key: "properties",
            value:
              '{"email_opt_in":"{opt_ins}","email_opt_out":"{opt_outs}","consent_framework":"{compliance_framework}","preference_updated_at":"{timestamp}","country":"{country}"}',
          },
        ],
        variables: [
          { name: "id", customName: "hubspot_update_id", type: "string" },
          {
            name: "properties.email_opt_in",
            customName: "confirmed_opt_ins",
            type: "string",
          },
        ],
      },
    }),
    n("4", "condition", "Any Opt-Outs from Critical Communications?", 760, {
      conditionDetails: {
        conditions: [
          {
            variable: "{has_critical_opt_out}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    emailNode(
      "5",
      "Email Confirm Preference Changes",
      1000,
      "{customer_email}",
      "Your communication preferences have been updated",
      "Hi,\\n\\nWe have updated your communication preferences as requested.\\n\\nCurrent opt-ins: {opt_ins}\\nCurrent opt-outs: {opt_outs}\\nCompliance framework: {compliance_framework}\\n\\nPlease note: You will continue to receive essential transactional and security communications as required by law. You can update your preferences at any time through our preference center.\\n\\nThank you!",
    ),
    slackNode(
      "6",
      "Slack Alert Compliance Team of Bulk Opt-Out",
      1240,
      ":shield: *Communication Preference Alert*\\n*Customer:* {customer_email}\\n*Country:* {country} ({compliance_framework})\\n*Opt-Outs:* {opt_outs}\\n*Critical Opt-Out:* {has_critical_opt_out}\\n*Bulk Change:* {is_bulk_change}\\nReview for compliance and potential churn signal.",
      400,
    ),
    sheetsNode(
      "7",
      "Audit Preference Changes",
      1480,
      "PreferenceAudit",
      '[["{customer_email}","{country}","{compliance_framework}","{opt_ins}","{opt_outs}","{has_critical_opt_out}","{is_bulk_change}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("4", "5", "output_2"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Manages customer communication preference updates with compliance awareness. Validates consent by jurisdiction (GDPR, CASL, CAN-SPAM), updates HubSpot contact preferences, flags critical opt-outs, confirms changes via email, alerts the compliance team in Slack about bulk opt-outs, and maintains a full audit trail in Google Sheets.",
};

const customer_escalation_detector = {
  nodes: [
    trigger("Webhook: Customer Interaction Event", "webhook_app", {
      source: "support_platform",
      event: "interaction.created",
      howItStarts:
        "Fires on any customer interaction event: support ticket, chat message, email, or call.",
      setup:
        "Configure your support platform to send a webhook for all customer interaction events.",
      inputFields: [],
    }),
    aiNode(
      "2",
      "AI Analyze Language Signals for Escalation",
      280,
      "You are a customer escalation detection expert. Analyze the customer interaction for language signals indicating: frustration_level (0-100), urgency_signals (boolean — mentions of deadlines, legal threats, executive escalation), churn_risk_signals (mentions of cancellation, competitor names, contract end), sentiment (very_negative, negative, neutral, positive), and trigger_phrases (specific concerning phrases found). Output as JSON.",
      "Analyze this customer interaction for escalation signals: {webhook_payload}",
      [
        { field: "frustration_level", as: "frustration_level", type: "number" },
        { field: "urgency_signals", as: "urgency_detected", type: "string" },
        { field: "churn_risk_signals", as: "churn_signals", type: "string" },
        { field: "sentiment", as: "interaction_sentiment", type: "string" },
        { field: "trigger_phrases", as: "trigger_phrases", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
      ],
    ),
    n(
      "3",
      "hitApi",
      "HubSpot Fetch Customer Value and Behavioral Signals",
      520,
      {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results[0].properties.annualrevenue",
              customName: "customer_arr",
              type: "number",
            },
            {
              name: "results[0].properties.open_tickets_count",
              customName: "open_tickets",
              type: "number",
            },
            {
              name: "results[0].properties.recent_interactions_count",
              customName: "recent_interactions",
              type: "number",
            },
            {
              name: "results[0].properties.health_score",
              customName: "health_score",
              type: "number",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
          ],
        },
      },
    ),
    n("4", "runCode", "Combine Signals into Escalation Score", 760, {
      codeDetails: {
        code: `const frustration = parseInt(inputs.frustration_level) || 0;
const urgency = inputs.urgency_detected === "true" ? 25 : 0;
const churnSignals = inputs.churn_signals ? 20 : 0;
const arr = parseFloat(inputs.customer_arr) || 0;
const openTickets = parseInt(inputs.open_tickets) || 0;
const healthScore = parseInt(inputs.health_score) || 50;
const recentInteractions = parseInt(inputs.recent_interactions) || 0;
// Behavioral boost: multiple open tickets, declining health, frequent contacts
const behavioralScore = Math.min(30, (openTickets > 3 ? 10 : 0) + (healthScore < 40 ? 10 : 0) + (recentInteractions > 5 ? 10 : 0));
const escalationScore = Math.min(100, frustration + urgency + churnSignals + behavioralScore);
const isHighEscalation = escalationScore > 75;
output.escalation_score = escalationScore;
output.is_high_escalation = isHighEscalation;
output.frustration_level = frustration;
output.customer_arr = arr;
output.customer_email = inputs.customer_email || "";
output.first_name = inputs.first_name || "";
output.company_name = inputs.company_name || "";
output.health_score = healthScore;
output.trigger_phrases = inputs.trigger_phrases || "";
output.interaction_sentiment = inputs.interaction_sentiment || "";`,
        outputVars: [
          { name: "escalation_score", type: "number" },
          { name: "is_high_escalation", type: "boolean" },
          { name: "frustration_level", type: "number" },
          { name: "customer_arr", type: "number" },
          { name: "customer_email", type: "string" },
          { name: "first_name", type: "string" },
          { name: "company_name", type: "string" },
          { name: "health_score", type: "number" },
          { name: "trigger_phrases", type: "string" },
          { name: "interaction_sentiment", type: "string" },
        ],
      },
    }),
    n("5", "condition", "Escalation Score > 75?", 1000, {
      conditionDetails: {
        conditions: [
          {
            variable: "{is_high_escalation}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    slackNode(
      "6",
      "Slack Alert CSM and Support Lead",
      1240,
      ":rotating_light: *Customer Escalation Detected — Score: {escalation_score}/100*\\n\\n*Customer:* {first_name} at {company_name} ({customer_email})\\n*ARR:* ${customer_arr}\\n*Health Score:* {health_score}\\n*Frustration Level:* {frustration_level}/100\\n*Sentiment:* {interaction_sentiment}\\n*Trigger Phrases:* {trigger_phrases}\\n\\nImmediate outreach recommended. Check open tickets and recent interactions.",
    ),
    emailNode(
      "7",
      "Email Proactive Outreach to Customer",
      1480,
      "{customer_email}",
      "{first_name}, we want to make sure you are getting the support you need",
      "Hi {first_name},\\n\\nI noticed you have been in touch with our team recently and I wanted to personally reach out to make sure we are addressing your needs effectively.\\n\\nIf there is anything I can do to help resolve your current concerns or improve your experience, please do not hesitate to reply to this email. I am here to help.\\n\\nBest regards,\\nYour Customer Success Manager",
    ),
    sheetsNode(
      "8",
      "Track Escalation Patterns",
      1720,
      "EscalationTracking",
      '[["{customer_email}","{company_name}","{customer_arr}","{escalation_score}","{frustration_level}","{interaction_sentiment}","{trigger_phrases}","{health_score}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Detects customer escalation risk by combining language analysis with behavioral signals. AI analyzes interaction language for frustration, urgency, and churn signals, HubSpot provides customer value and behavioral data, a composite escalation score is computed, and high-risk cases (score >75) trigger immediate Slack alerts to CSM/support leads plus proactive customer outreach.",
};

const customer_service_recovery_after_bad_experience = {
  nodes: [
    trigger("Webhook: Negative CSAT or Complaint Received", "webhook_app", {
      source: "feedback_platform",
      event: "negative_feedback.received",
      howItStarts:
        "Fires when a negative CSAT score (1-2) or formal complaint is received.",
      setup:
        "Configure your feedback platform to send a webhook when CSAT scores 1-2 or complaints are filed.",
      inputFields: [],
    }),
    n(
      "2",
      "hitApi",
      "HubSpot Fetch Customer Profile and Interaction History",
      280,
      {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.annualrevenue",
              customName: "customer_arr",
              type: "number",
            },
            {
              name: "results[0].properties.customer_tenure_months",
              customName: "tenure_months",
              type: "number",
            },
            {
              name: "results[0].properties.total_support_tickets",
              customName: "total_tickets",
              type: "number",
            },
            {
              name: "results[0].properties.prior_complaints",
              customName: "prior_complaints",
              type: "number",
            },
          ],
        },
      },
    ),
    aiNode(
      "3",
      "AI Analyze Root Cause and Recommend Recovery",
      520,
      "You are a customer service recovery expert. Analyze the negative feedback, customer history, and interaction context to determine: root_cause (response_time, agent_error, process_failure, product_bug, miscommunication, policy_issue), severity (low, medium, high, critical — consider repeat complaints and customer value), recovery_approach (apology, credit, executive_outreach, process_fix, or combined), and personalized_message (a draft recovery message addressing their specific concern). Output as JSON.",
      "Negative feedback: {feedback_text}, CSAT Score: {csat_score}, Customer: {first_name}, ARR: ${customer_arr}, Tenure: {tenure_months} months, Prior complaints: {prior_complaints}, Total tickets: {total_tickets}",
      [
        { field: "root_cause", as: "root_cause", type: "string" },
        { field: "severity", as: "issue_severity", type: "string" },
        { field: "recovery_approach", as: "recovery_approach", type: "string" },
        {
          field: "personalized_message",
          as: "recovery_message",
          type: "string",
        },
      ],
    ),
    n("4", "runCode", "Select Recovery Action by Severity", 760, {
      codeDetails: {
        code: `const rootCause = inputs.root_cause || "miscommunication";
const severity = inputs.issue_severity || "medium";
const approach = inputs.recovery_approach || "apology";
const arr = parseFloat(inputs.customer_arr) || 0;
const priorComplaints = parseInt(inputs.prior_complaints) || 0;
const requiresCompensation = severity === "critical" || severity === "high" || priorComplaints > 2;
const compensationMap = {
  low: { action: "Personal apology email from team lead", compensation: "none" },
  medium: { action: "Personal apology + priority support for 30 days", compensation: "none" },
  high: { action: "Executive apology + account credit", compensation: "10% monthly credit" },
  critical: { action: "VP-level outreach + significant credit + process review", compensation: "1 month free + dedicated support" },
};
const recovery = compensationMap[severity] || compensationMap.medium;
output.recovery_action = recovery.action;
output.compensation = recovery.compensation;
output.requires_compensation = requiresCompensation;
output.root_cause = rootCause;
output.severity = severity;
output.customer_email = inputs.customer_email || "";
output.first_name = inputs.first_name || "";
output.recovery_message = inputs.recovery_message || "";
output.customer_arr = arr;`,
        outputVars: [
          { name: "recovery_action", type: "string" },
          { name: "compensation", type: "string" },
          { name: "requires_compensation", type: "boolean" },
          { name: "root_cause", type: "string" },
          { name: "severity", type: "string" },
          { name: "customer_email", type: "string" },
          { name: "first_name", type: "string" },
          { name: "recovery_message", type: "string" },
          { name: "customer_arr", type: "number" },
        ],
      },
    }),
    n("5", "condition", "Requires Monetary Compensation?", 1000, {
      conditionDetails: {
        conditions: [
          {
            variable: "{requires_compensation}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    n("6", "approvalGate", "Manager Approves Compensation", 1240, {
      approvalDetails: {
        approver: "support-manager",
        timeout: 24,
        timeoutUnit: "hours",
      },
    }),
    emailNode(
      "7",
      "Email Personalized Recovery Message",
      1480,
      "{customer_email}",
      "{first_name}, we owe you an apology",
      "Hi {first_name},\\n\\n{recovery_message}\\n\\nTo make this right, we are taking the following action: {recovery_action}\\n\\nCompensation: {compensation}\\n\\nI take this personally and want to ensure this does not happen again. Would you be open to a brief call to discuss how we can better serve you?\\n\\nSincerely,\\nCustomer Success Team",
    ),
    sheetsNode(
      "8",
      "Track Recovery Success Rate",
      1720,
      "ServiceRecovery",
      '[["{customer_email}","{first_name}","{customer_arr}","{root_cause}","{severity}","{recovery_action}","{compensation}","{requires_compensation}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("5", "7", "output_2"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Orchestrates service recovery after negative customer experiences. Fetches customer profile from HubSpot, AI analyzes the root cause and recommends a severity-appropriate recovery approach, selects the recovery action (apology, credit, executive outreach), gates compensation through manager approval, sends a personalized recovery email, and tracks recovery success rates.",
};

const health_score_decline_customer_success_play = {
  nodes: [
    trigger("Schedule: Daily Health Score Check at 8 AM", "schedule", {
      schedule: "0 8 * * *",
      howItStarts:
        "Runs daily at 8 AM to identify customers with declining health scores.",
      setup:
        "No external setup needed. Runs daily to check for health score declines in HubSpot.",
      inputFields: [],
    }),
    n(
      "2",
      "hitApi",
      "HubSpot Fetch Customers with Declining Health Scores",
      280,
      {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"health_score_change_30d","operator":"LT","value":"-10"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","annualrevenue","health_score","health_score_change_30d","product_usage_pct","support_tickets_open","last_login_date","csm_owner"]',
            },
            { key: "limit", value: "50" },
            {
              key: "sorts",
              value:
                '[{"propertyName":"health_score_change_30d","direction":"ASCENDING"}]',
            },
          ],
          variables: [
            { name: "total", customName: "declining_count", type: "number" },
            {
              name: "results",
              customName: "declining_customers",
              type: "string",
            },
          ],
        },
      },
    ),
    aiNode(
      "3",
      "AI Analyze Health-Change Cause per Customer",
      520,
      "You are a customer success analytics expert. For each declining customer, analyze the likely cause of health score decline: usage_drop (login frequency or feature usage decreased), support_increase (rising ticket volume or escalations), engagement_decrease (fewer emails opened, events attended, or community activity), billing_issue (payment failures or plan changes), or combined. For each customer, provide: cause, risk_level (low/medium/high/critical), and recommended_play (specific CSM action). Output as JSON with a customers array.",
      "Analyze health decline causes for these customers: {declining_customers}",
      [
        { field: "customers", as: "analyzed_customers", type: "string" },
        { field: "critical_count", as: "critical_count", type: "number" },
        {
          field: "primary_cause_distribution",
          as: "cause_distribution",
          type: "string",
        },
      ],
    ),
    n("4", "runCode", "Generate CSM Playbook per Customer", 760, {
      codeDetails: {
        code: `const analyzed = inputs.analyzed_customers || "[]";
const criticalCount = parseInt(inputs.critical_count) || 0;
const totalDeclining = parseInt(inputs.declining_count) || 0;
const causeDistribution = inputs.cause_distribution || "";
const hasCriticalDrops = criticalCount > 0;
// Determine severity thresholds
const majorDropThreshold = 20;
output.total_declining = totalDeclining;
output.critical_count = criticalCount;
output.has_critical_drops = hasCriticalDrops;
output.cause_distribution = causeDistribution;
output.analyzed_customers = analyzed;
output.report_date = new Date().toISOString().split("T")[0];`,
        outputVars: [
          { name: "total_declining", type: "number" },
          { name: "critical_count", type: "number" },
          { name: "has_critical_drops", type: "boolean" },
          { name: "cause_distribution", type: "string" },
          { name: "analyzed_customers", type: "string" },
          { name: "report_date", type: "string" },
        ],
      },
    }),
    n("5", "condition", "Any Health Score Dropped > 20 Points?", 1000, {
      conditionDetails: {
        conditions: [
          {
            variable: "{has_critical_drops}",
            operator: "equals",
            value: "true",
          },
        ],
      },
    }),
    slackNode(
      "6",
      "Slack Alert CSM with Customer Context",
      1240,
      ":chart_with_downwards_trend: *Critical Health Score Decline Alert — {report_date}*\\n\\n*{critical_count} customers with critical drops (>20 pts)*\\n*{total_declining} total declining customers*\\n\\n*Cause Distribution:* {cause_distribution}\\n\\n*Critical Customers:* Review the CSM briefing email for detailed playbooks per account.\\n\\nImmediate outreach recommended for critical accounts.",
    ),
    emailNode(
      "7",
      "Email CSM Briefing with Account Plan Updates",
      1480,
      "{csm_email}",
      "Health Score Alert: {critical_count} critical accounts need attention — {report_date}",
      "Hi,\\n\\nYour daily health score report has identified accounts requiring attention:\\n\\n*Summary:*\\n- Total declining: {total_declining}\\n- Critical (>20pt drop): {critical_count}\\n- Primary causes: {cause_distribution}\\n\\n*Detailed Analysis and Recommended Playbooks:*\\n{analyzed_customers}\\n\\nPlease prioritize critical accounts for outreach today. Update account notes with your intervention plan.\\n\\nCustomer Success Operations",
    ),
    sheetsNode(
      "8",
      "Track Intervention Effectiveness",
      1720,
      "HealthScoreInterventions",
      '[["{report_date}","{total_declining}","{critical_count}","{cause_distribution}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5"),
    e("5", "6", "output_1"),
    e("6", "7"),
    e("7", "8"),
  ],
  summary:
    "Runs daily to identify and action customers with declining health scores. Fetches declining accounts from HubSpot, AI analyzes the cause per customer (usage drop, support increase, engagement decrease), generates CSM playbooks, alerts in Slack for critical drops (>20 points), emails detailed briefings to CSMs, and tracks intervention effectiveness over time.",
};

const help_center_article_no_resolution_follow_up = {
  nodes: [
    trigger(
      "Webhook: Customer Viewed Article Then Submitted Ticket",
      "webhook_app",
      {
        source: "help_center",
        event: "article_then_ticket",
        howItStarts:
          "Fires when a customer views a help center article and then submits a support ticket within 30 minutes, indicating the article did not resolve their issue.",
        setup:
          "Configure your help center analytics to detect the pattern of article view followed by ticket submission and send a webhook.",
        inputFields: [],
      },
    ),
    aiNode(
      "2",
      "AI Analyze Article vs Ticket to Identify Content Gap",
      280,
      "You are a help center content analyst. Compare the article the customer viewed with the ticket they submitted to identify content gaps. Output: article_title, article_topic, ticket_topic, gap_type (missing_info, outdated, too_complex, wrong_article, incomplete_steps), gap_description (specific content that is missing or wrong), needs_update (true/false), customer_email, and ticket_id. Output as JSON.",
      "Analyze the gap between article viewed and ticket submitted: {webhook_payload}",
      [
        { field: "article_title", as: "article_title", type: "string" },
        { field: "article_topic", as: "article_topic", type: "string" },
        { field: "ticket_topic", as: "ticket_topic", type: "string" },
        { field: "gap_type", as: "gap_type", type: "string" },
        { field: "gap_description", as: "gap_description", type: "string" },
        { field: "needs_update", as: "needs_update", type: "string" },
        { field: "customer_email", as: "customer_email", type: "string" },
        { field: "ticket_id", as: "ticket_id", type: "string" },
      ],
    ),
    n(
      "3",
      "runCode",
      "Determine If Article Needs Update or Alternative Resource",
      520,
      {
        codeDetails: {
          code: `const gapType = inputs.gap_type || "missing_info";
const needsUpdate = inputs.needs_update === "true";
const articleTitle = inputs.article_title || "";
const gapDescription = inputs.gap_description || "";
const customerEmail = inputs.customer_email || "";
const ticketId = inputs.ticket_id || "";
const hasArticleGap = needsUpdate || gapType === "outdated" || gapType === "incomplete_steps";
const alternativeResource = gapType === "wrong_article" ? "specialist_redirect" : (gapType === "too_complex" ? "video_walkthrough" : "updated_article");
output.has_article_gap = hasArticleGap;
output.alternative_resource = alternativeResource;
output.gap_type = gapType;
output.gap_description = gapDescription;
output.article_title = articleTitle;
output.customer_email = customerEmail;
output.ticket_id = ticketId;`,
          outputVars: [
            { name: "has_article_gap", type: "boolean" },
            { name: "alternative_resource", type: "string" },
            { name: "gap_type", type: "string" },
            { name: "gap_description", type: "string" },
            { name: "article_title", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "ticket_id", type: "string" },
          ],
        },
      },
    ),
    n("4", "condition", "Article Content Gap Identified?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{has_article_gap}", operator: "equals", value: "true" },
        ],
      },
    }),
    emailNode(
      "5",
      "Email Offer Alternative Resources",
      1000,
      "{customer_email}",
      "Additional help for your issue (Ticket #{ticket_id})",
      'Hi,\\n\\nWe noticed you viewed our article "{article_title}" before submitting your support request. We want to make sure you get the help you need.\\n\\nHere are some additional resources that might help:\\n- Our specialist team is reviewing your ticket for a faster resolution\\n- Alternative resource: {alternative_resource}\\n\\nYour ticket #{ticket_id} is being handled with priority. We will follow up shortly.\\n\\nThank you for your patience!',
    ),
    slackNode(
      "6",
      "Slack Alert Content Team About Article Gap",
      1240,
      ":page_facing_up: *Help Center Article Gap Detected*\\n*Article:* {article_title}\\n*Gap Type:* {gap_type}\\n*Gap Description:* {gap_description}\\n*Ticket that triggered this:* #{ticket_id}\\n*Customer:* {customer_email}\\n\\nPlease review and update the article to address this content gap.",
    ),
    sheetsNode(
      "7",
      "Track Article Effectiveness",
      1480,
      "ArticleEffectiveness",
      '[["{article_title}","{article_topic}","{ticket_topic}","{gap_type}","{gap_description}","{ticket_id}","{customer_email}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Identifies help center content gaps when customers view an article but still submit a ticket. AI compares article content with the ticket to identify the gap type (missing info, outdated, too complex), offers alternative resources to the customer, alerts the content team about the specific gap, and tracks article effectiveness for continuous improvement.",
};

const high_risk_customer_internal_account_alert = {
  nodes: [
    trigger("Schedule: Daily Risk Assessment at 7 AM", "schedule", {
      schedule: "0 7 * * *",
      howItStarts:
        "Runs daily at 7 AM to identify customers with multiple risk signals.",
      setup:
        "No external setup needed. Runs daily to compute composite risk scores from HubSpot data.",
      inputFields: [],
    }),
    n(
      "2",
      "hitApi",
      "HubSpot Identify Customers with Multiple Risk Signals",
      280,
      {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"health_score","operator":"LT","value":"50"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","lastname","company","annualrevenue","health_score","product_usage_pct","support_tickets_open","days_since_last_login","payment_failures_count","nps_score","contract_end_date","csm_owner"]',
            },
            { key: "limit", value: "50" },
            {
              key: "sorts",
              value:
                '[{"propertyName":"health_score","direction":"ASCENDING"}]',
            },
          ],
          variables: [
            { name: "total", customName: "at_risk_count", type: "number" },
            {
              name: "results",
              customName: "at_risk_customers",
              type: "string",
            },
          ],
        },
      },
    ),
    n(
      "3",
      "runCode",
      "Compute Composite Risk Score from Multiple Signals",
      520,
      {
        codeDetails: {
          code: `const customers = inputs.at_risk_customers || "[]";
const totalAtRisk = parseInt(inputs.at_risk_count) || 0;
// Composite scoring: usage (25%), support (20%), billing (20%), engagement (20%), NPS (15%)
// For aggregate reporting
let criticalCount = 0;
let highCount = 0;
const reportDate = new Date().toISOString().split("T")[0];
// Threshold: risk score > 80 is critical
output.total_at_risk = totalAtRisk;
output.critical_count = criticalCount;
output.high_risk_count = highCount;
output.report_date = reportDate;
output.at_risk_customers = customers;
output.has_critical = totalAtRisk > 0;`,
          outputVars: [
            { name: "total_at_risk", type: "number" },
            { name: "critical_count", type: "number" },
            { name: "high_risk_count", type: "number" },
            { name: "report_date", type: "string" },
            { name: "at_risk_customers", type: "string" },
            { name: "has_critical", type: "boolean" },
          ],
        },
      },
    ),
    n("4", "condition", "Any Risk Score > 80?", 760, {
      conditionDetails: {
        conditions: [
          { variable: "{has_critical}", operator: "equals", value: "true" },
        ],
      },
    }),
    slackNode(
      "5",
      "Slack Alert Account Team with Risk Summary",
      1000,
      ":rotating_light: *Daily High-Risk Customer Alert — {report_date}*\\n\\n*{total_at_risk} customers at elevated risk*\\n*{critical_count} critical (risk > 80)*\\n*{high_risk_count} high (risk 60-80)*\\n\\nRisk signals: low health scores, declining usage, open support tickets, payment issues, low NPS.\\n\\nReview the detailed briefing email for per-account action plans.",
    ),
    emailNode(
      "6",
      "Email CSM with Detailed Risk Briefing",
      1240,
      "{csm_email}",
      "High-Risk Account Alert: {total_at_risk} accounts need attention — {report_date}",
      "Hi,\\n\\nYour daily risk assessment has identified {total_at_risk} accounts with elevated risk signals.\\n\\nCritical accounts (risk > 80): {critical_count}\\nHigh-risk accounts (risk 60-80): {high_risk_count}\\n\\nDetailed Account Analysis:\\n{at_risk_customers}\\n\\nRecommended Actions:\\n1. Contact critical accounts within 24 hours\\n2. Review and update account success plans\\n3. Escalate any accounts with contract renewal within 60 days\\n\\nCustomer Success Operations",
    ),
    sheetsNode(
      "7",
      "Track Risk-to-Churn Correlation",
      1480,
      "RiskTracking",
      '[["{report_date}","{total_at_risk}","{critical_count}","{high_risk_count}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Runs a daily risk assessment to identify customers with composite risk scores above 80. Fetches customers with low health scores from HubSpot, computes composite risk from usage, support, billing, engagement, and NPS signals, alerts account teams in Slack, sends detailed CSM briefings, and tracks risk-to-churn correlation over time.",
};

const onboarding_support_check_in = {
  nodes: [
    trigger("Webhook: Customer Reaches Onboarding Milestone", "webhook_app", {
      source: "product_platform",
      event: "onboarding.milestone_reached",
      howItStarts:
        "Fires when a customer reaches an onboarding milestone (account setup, first integration, first workflow, team invite).",
      setup:
        "Configure your product platform to send webhooks at key onboarding milestones.",
      inputFields: [],
    }),
    n(
      "2",
      "hitApi",
      "HubSpot Fetch Onboarding Progress and Adoption Data",
      280,
      {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "results[0].properties.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "results[0].properties.firstname",
              customName: "first_name",
              type: "string",
            },
            {
              name: "results[0].properties.company",
              customName: "company_name",
              type: "string",
            },
            {
              name: "results[0].properties.onboarding_step",
              customName: "current_step",
              type: "string",
            },
            {
              name: "results[0].properties.onboarding_start_date",
              customName: "start_date",
              type: "string",
            },
            {
              name: "results[0].properties.features_activated",
              customName: "features_activated",
              type: "number",
            },
            {
              name: "results[0].properties.expected_step_by_now",
              customName: "expected_step",
              type: "string",
            },
            {
              name: "results[0].properties.csm_owner",
              customName: "csm_owner",
              type: "string",
            },
          ],
        },
      },
    ),
    aiNode(
      "3",
      "AI Assess Onboarding Health and Identify Stuck Points",
      520,
      "You are an onboarding success analyst. Assess the customer's onboarding health by comparing actual progress against expected pace. Identify: onboarding_health (on_track, slightly_behind, at_risk, critical), stuck_points (specific steps or features where the customer appears stuck), days_behind (number of days behind expected pace, 0 if on track), recommended_guidance (specific actionable advice to help them progress), and next_milestone (what they should complete next). Output as JSON.",
      "Customer: {first_name} at {company_name}, Current step: {current_step}, Expected step: {expected_step}, Start date: {start_date}, Features activated: {features_activated}",
      [
        { field: "onboarding_health", as: "onboarding_health", type: "string" },
        { field: "stuck_points", as: "stuck_points", type: "string" },
        { field: "days_behind", as: "days_behind", type: "number" },
        {
          field: "recommended_guidance",
          as: "recommended_guidance",
          type: "string",
        },
        { field: "next_milestone", as: "next_milestone", type: "string" },
      ],
    ),
    n("4", "condition", "Behind Expected Onboarding Pace?", 760, {
      conditionDetails: {
        conditions: [
          {
            variable: "{onboarding_health}",
            operator: "not_equals",
            value: "on_track",
          },
        ],
      },
    }),
    emailNode(
      "5",
      "Email Proactive Check-In with Personalized Guidance",
      1000,
      "{customer_email}",
      "{first_name}, here is some help with your next onboarding step",
      "Hi {first_name},\\n\\nWelcome aboard! I am checking in to make sure your onboarding is going smoothly.\\n\\nI noticed you have completed: {current_step}\\nYour next milestone: {next_milestone}\\n\\nHere is some guidance to help you move forward:\\n{recommended_guidance}\\n\\nIf you are running into any issues, I am here to help. You can reply to this email or book a quick call at {calendar_link}.\\n\\nBest regards,\\nYour Onboarding Team",
    ),
    slackNode(
      "6",
      "Slack Notify CSM of At-Risk Onboarding",
      1240,
      ":warning: *At-Risk Onboarding Alert*\\n*Customer:* {first_name} at {company_name}\\n*Email:* {customer_email}\\n*Status:* {onboarding_health}\\n*Days Behind:* {days_behind}\\n*Current Step:* {current_step} (Expected: {expected_step})\\n*Stuck Points:* {stuck_points}\\n*CSM:* {csm_owner}\\nPlease reach out to assist with onboarding.",
    ),
    sheetsNode(
      "7",
      "Track Onboarding Velocity",
      1480,
      "OnboardingVelocity",
      '[["{customer_email}","{company_name}","{current_step}","{expected_step}","{onboarding_health}","{days_behind}","{stuck_points}","{features_activated}","=NOW()"]]',
    ),
  ],
  edges: [
    e("1", "2"),
    e("2", "3"),
    e("3", "4"),
    e("4", "5", "output_1"),
    e("5", "6"),
    e("6", "7"),
  ],
  summary:
    "Monitors onboarding progress and proactively intervenes when customers fall behind. Fetches onboarding data from HubSpot, AI assesses onboarding health and identifies stuck points, sends personalized check-in emails with specific guidance when customers are behind pace, alerts the assigned CSM in Slack, and tracks onboarding velocity metrics in Google Sheets.",
};

/* ───────────────────────────────────────────────────────────────────────────
 * EXPORT
 * ─────────────────────────────────────────────────────────────────────────── */

export const ELITE_BATCH21_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // S2 Remaining (4)
  "Ticket Creation Bot": ticket_creation_bot,
  "Troubleshooting Wizard": troubleshooting_wizard,
  "Zendesk Ticket to Slack Alert": zendesk_ticket_to_slack_alert,
  "Self-Service Article Recommendation": self_service_article_recommendation,
  // S3 Surveys/CSAT/NPS (13)
  "CSAT Survey After Resolution": csat_survey_after_resolution,
  "CSAT Survey After Support Ticket": csat_survey_after_support_ticket,
  "CSAT Survey Post Chat": csat_survey_post_chat,
  "Chatbot Analytics Digest": chatbot_analytics_digest,
  "Customer Effort Score Tracker": customer_effort_score_tracker,
  "Customer Feedback Survey": customer_feedback_survey,
  "Customer Survey Distribution Bot": customer_survey_distribution_bot,
  "NPS Detractor Recovery Workflow": nps_detractor_recovery_workflow,
  "NPS Passive Education Nurture": nps_passive_education_nurture,
  "NPS Survey After Milestone": nps_survey_after_milestone,
  "NPS Survey Bot": nps_survey_bot,
  "Service Appointment Follow-Up": service_appointment_follow_up,
  "WebEngage Feedback Collection Flow": webengage_feedback_collection_flow,
  // S4 Recovery/Churn (10)
  "Cancellation Prevention Bot": cancellation_prevention_bot,
  "Concierge Chat Follow-Up": concierge_chat_follow_up,
  "Customer Advisory Board Invite": customer_advisory_board_invite,
  "Customer Communication Preference Center":
    customer_communication_preference_center,
  "Customer Escalation Detector": customer_escalation_detector,
  "Customer Service Recovery After Bad Experience":
    customer_service_recovery_after_bad_experience,
  "Health-Score Decline Customer Success Play":
    health_score_decline_customer_success_play,
  "Help-Center Article No-Resolution Follow-Up":
    help_center_article_no_resolution_follow_up,
  "High-Risk Customer Internal Account Alert":
    high_risk_customer_internal_account_alert,
  "Onboarding Support Check-In": onboarding_support_check_in,
};
